import { useMemo } from "react";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios, { isCancel } from "axios";
import type { Options, UseAxiosResult } from "axios-hooks";
import { makeUseAxios } from "axios-hooks";
import { debounce } from "lodash-es";
import { z } from "zod";

import type {
  CuratedPlaceOfSafety,
  DateRangeUnbranded,
  RecordPolicePresenceV2Action,
  ShareableIncidentDataV1Response,
  ValidateShareCodeV1Request,
  ValidateShareCodeV1Response,
} from "@thalamos/common";
import { parseAxiosResponse } from "@thalamos/common";
import type {
  AcceptInterviewInviteInput,
  AcceptTermsAndConditionsRequest,
  AcceptVisionIncidentResponse,
  AddFavouriteRecipientRequest,
  AddFileNote,
  AddInterviewParticipantInput,
  AddOrganisationMemberRequest,
  AddResidentRequest,
  AddTeamMemberRequest,
  AdjustSection12PaymentRequestInput,
  AdminGetDataPermissionInfoResponse,
  AdminPatientDetailsRequest,
  AdminPatientDetailsResponse,
  AuditTrailEntry,
  AuditTrailEntryType,
  CalendarSyncConnection,
  CapacityStateWithContext,
  ChoiceDifferencesEntry,
  ClaimOutcomeFormRequest,
  ClaimOutcomeFormResponse,
  CompleteOutcomeFormResponse,
  ConfirmParticipantInput,
  ConfirmSection12PhoneRequest,
  ConnectCalendarFeedRequest,
  CorrectS132AttemptRequest,
  CreateAndClaimOneTimeTokenForRioClickThroughInstanceRequest,
  CreateAndClaimOneTimeTokenForRioClickThroughInstanceResponse,
  CreateDraft,
  CreateExternalPatientDemograhicsPullEventRequest,
  CreateExternalPatientDemograhicsPullEventResponse,
  CreateFormRequest,
  CreateInterviewInput,
  CreateLondonCareRecordInstanceRequest,
  CreateMentalHealthCareProviderRequest,
  CreateOrganisationRequest,
  // Outcome form types
  CreateOutcomeFormRequest,
  CreateOutcomeFormResponse,
  CreatePatientFromExternalIdRequest,
  CreatePatientFromExternalIdResponse,
  CreatePatientRequest,
  CreatePlaceOfSafetyRequest,
  CreateSection12LanguageRequest,
  CreateSection12PaymentRequestInput,
  CreateSection12SpecialismRequest,
  CreateSingleUseActionTokenRequest,
  CreateSoadRequest,
  CreateTeamRequest,
  CreateTermsAndConditionsRequest,
  CreateTrustRequest,
  CreateWorkItemRequest,
  DashboardData,
  DashboardTrustsResponse,
  DataSharingPartnership,
  DataSharingPartnershipWithDataControllers,
  DeleteOrganisationRequest,
  DeleteSoadContextRequest,
  DeleteTeamRequest,
  DoctorInterviewView,
  EprUserLinkSearchResponse,
  ExchangeCalendarSyncCodeRequest,
  ExtendedOrganisation,
  ExtendedPatient,
  ExtendedTeam,
  ExtendedThalamosUser,
  FileNote,
  FormContextData,
  FormPartAssignmentOptionsSearch,
  GeneratePresignedUrlRequestBody,
  GeneratePresignedUrlResponseBody,
  GenericConfiguration,
  GetAllSingleUseActionTokensResponse,
  GetCalendarSyncAuthUrlRequest,
  GetCalendarSyncAuthUrlResponse,
  GetCalendarSyncConnectionsResponse,
  GetCareHomeGrantsResponse,
  GetDrFinderAssessingOrganisationsResponse,
  GetExternalPatientDemograhicsPullEventResponse,
  GetExternalPatientFormPushEventResponse,
  GetExternalPatientLinksResponse,
  GetFormDraftResponse,
  GetFormResponse,
  GetOrganisationResponse,
  GetPatientMhaStateResponse,
  GetPatientResponse,
  GetSection12AvailabilityOverrideBlocksResponse,
  GetSection136PlaceOfSafetyResponse,
  GetSingleUseActionTokenResponse,
  GetTortusAvtLaunchTokenRequest,
  GetTortusAvtLaunchTokenResponse,
  GetWorkResponse,
  GuestSessionAccreditation,
  GuestUserConversionRequest,
  HospitalMhlAutoRouteConfig,
  InterviewVerification,
  InterviewWithParticipants,
  LimitedUpdateSection12PaymentRequestInput,
  LocalAuthorityPaymentConfiguration,
  LondonCareRecordConfig,
  ManagedLocation,
  ManagedLocationsResponse,
  Membership,
  MentalHealthCareProvider,
  MergeTeamPreviewResponse,
  MergeTeamRequest,
  MhaDashboardDataParams,
  MhaDashboardDataResponse,
  MhaStatus,
  NotificationsResponse,
  Organisation,
  OrganisationConfiguration,
  OrganisationSupersetDashboard,
  OrgsAndTeamsResult,
  OutcomeFormSummary,
  Patient,
  PatientDemographicsAlertsResponse,
  PatientIndexQuery,
  PatientIndexSearchResult,
  PatientIndexSearchResultV2,
  PatientListResponse,
  PatientListSortBy,
  PatientListSortDirection,
  PatientMergeInfo,
  PatientSearchRequestV2,
  PatientStateCsvRequest,
  PatientStateWithContext,
  PatientTimelineResponse,
  PatientUnmergeIds,
  PeekClickThroughTokenResponse,
  PreviewVisionIncidentReponse,
  ReasonableAdjustmentsFlagResponse,
  RecordCapacityStatusRequest,
  RecordS132AttemptRequest,
  RecordUserSessionEventRequest,
  RedeemOneTimeTokenForGenericClickThroughInstanceRequest,
  RedeemOneTimeTokenForGenericClickThroughInstanceResponse,
  RedeemOneTimeTokenForRioClickThroughInstanceRequest,
  RedeemOneTimeTokenForRioClickThroughInstanceResponse,
  RedeemOneTimeTokenForTppClickThroughInstanceRequest,
  RedeemOneTimeTokenForTppClickThroughInstanceResponse,
  RedoReasonRequest,
  RegisterQueryBody,
  RegisterResponse,
  RejectInterviewInviteInput,
  RemoveOrganisationMemberRequest,
  RemoveResidentRequest,
  RemoveTeamMemberRequest,
  ReportContextParams,
  RequestAmendRequest,
  RequestRedoRequest,
  RioConfiguration,
  RioConnectionTestResponse,
  RioUploadData,
  RouteTimeResult,
  S132Record,
  SaveSearchReasoningV2Request,
  SaveSearchReasoningV2Response,
  SearchReasoning,
  Section12DoctorAdminSearchResult,
  Section12DoctorSearchResultUnbranded,
  Section12Language,
  Section12PaymentRequest,
  Section12PaymentRequestChange,
  Section12PaymentRequestReceiptDownloadResponse,
  Section12PaymentRequestStatus,
  Section12PaymentRequestStatusFilter,
  Section12PaymentRequestWithDetails,
  Section12PhoneVerificationStatus,
  Section12Specialism,
  Section12TeamInvoicing,
  SendSection12PhoneVerificationCodeRequest,
  SetCareHomeGrantsRequest,
  SetDrFinderAssessingOrganisationsRequest,
  SetSection12AvailabilityBlocksRequestUnbranded,
  SetSection12AvailabilityOverrideDatesRequestUnbranded,
  SetSection12ContactPreferencesRequest,
  SetSection12GenderRequest,
  SetSection12LanguagesRequest,
  SetSection12LocalAuthorityMappingsRequest,
  SetSection12MaxTravelTimeRequest,
  SetSection12SpecialismsRequest,
  SetSection12SpecialismsWithLevelsRequest,
  SetSection12TrustAffiliationRequest,
  SignDraftRequest,
  SignedFormsCsvParams,
  SignSoadAssessmentRequest,
  SoadListResponse,
  SupersetDashboard,
  SupersetDashboardExportResponse,
  SupersetDashboardSearchResult,
  SupersetEmbedConfig,
  TeamCareHomeGrant,
  TemplateData,
  TermsAndConditions,
  TermsAndConditionsResponse,
  TppConfiguration,
  TransferResidentRequest,
  Trust,
  UnfinaliseWorkItemRequest,
  UpdateExternalPatientDemograhicsPullEventRequest,
  UpdateExternalPatientDemograhicsPullEventResponse,
  UpdateFormContextAssessmentLocationRequest,
  UpdateFormContextOfficialReferenceIdRequest,
  UpdateInterviewInput,
  UpdateMentalHealthCareProviderRequest,
  UpdateOrganisationRequest,
  UpdatePatientRequest,
  UpdatePatientStateWithManualEventRequest,
  UpdatePlaceOfSafetyRequest,
  UpdateSection12LanguageRequest,
  UpdateSection12PaymentRequestAdminStatusInput,
  UpdateSection12PaymentRequestInput,
  UpdateSection12PaymentRequestStatusInput,
  UpdateSection12SpecialismRequest,
  UpdateTeamRequest,
  UpdateTrustRequest,
  UploadFormRequest,
  UpsertGenericInstanceRequest,
  UpsertRioInstanceRequest,
  UpsertSupersetDashboardRequest,
  UpsertTppInstanceRequest,
  UserCreate,
  UserDeviceSession,
  UserOrganisationConfiguration,
  UserOrganisationConfigurationSummary,
  UserOrTeamSearchResult,
  UserSearchResult,
  UserUpdate,
  UseSingleUseActionTokenResponse,
  UUID,
  VisionAcceptIncidentRequest,
  VisionAccessRequest,
  VisionAccessRequestResponse,
  VisionIncidentIdResponse,
  WithdrawS132AttemptRequest,
} from "@thalamos/aspire-common";
import {
  guestUserLoginHint,
  MentalHealthCareProviderSchema,
  notificationChannelFns,
  NotificationEvents,
  type ConnectivityResponse,
  type PathwayCohortQuery,
  type PathwayCohortStatsResponse,
  type PathwayDetentionPeriodsResponse,
  type PathwayFacetsResponse,
  type PathwayPatientPathwaysResponse,
  type PathwayPatientRowsQuery,
  type PathwayPatientRowsResponse,
  type PathwaySinglePatientDetentionPeriodsResponse,
  type PathwayTimelinesResponse,
} from "@thalamos/aspire-common";

import { renderErrorToast } from "./components/design-system/index.js";
import { config } from "./config.js";
import { okResponseData } from "./helpers/okResponseData.js";
import { useChannel } from "./lib/pusher/useChannel.js";
import { useEvent } from "./lib/pusher/useEvent.js";
import { routeFns } from "./routePaths.js";
import { checkVersionMismatch } from "./versionCheck.js";

let currentUserId: string | null = null;

const parseMentalHealthCareProviderGetAll = parseAxiosResponse(
  z.array(MentalHealthCareProviderSchema),
);

export type FhirApiClientResponse = {
  id: string;
  name: string;
  description: string | null;
  publicKeyJwks: Record<string, unknown>;
  allowedOrganisationIds: string[];
  allowedScopes: string[];
  retentionMonths: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type OdsOrganisationDetails = {
  name: string;
  addressLine1: string | null;
  town: string | null;
  postCode: string | null;
  telephone: string | null;
  /** The country of the organisation's address as ODS spells it, such as `ENGLAND`. */
  country: string | null;
  /**
   * Whether ODS gives the organisation the prison prescribing cost centre role:
   * the healthcare a prison gives its prisoners, registered as their practice.
   */
  holdsPrisonRole: boolean;
};

export type LocalAuthorityDetails = {
  code: string;
  name: string | null;
};

export type IcbDetails = {
  odsCode: string;
  name: string;
  subIcbLocation: { odsCode: string; name: string } | null;
};

const apiAxios = axios.create({
  baseURL: config.backendUrl,

  // Important for cookies etc.
  withCredentials: true,
  validateStatus: (status) => status >= 200 && status < 500,
});

apiAxios.interceptors.request.use((config) => {
  if (
    currentUserId &&
    ["get", "post", "put", "delete", "patch"].includes(
      config.method?.toLowerCase() || "",
    )
  ) {
    config.headers["X-Thalamos-User-Id"] = currentUserId;
  }
  return config;
});

export const redirectToLogin = () => {
  // Normalise consecutive slashes to prevent redirect loops when the user
  // navigates to a URL like https://example.com//.
  const pathname = window.location.pathname.replace(/\/+/g, "/");
  const originalQs = new URLSearchParams(window.location.search);

  const returnTo = `${pathname}${
    originalQs.size > 0 ? `?${originalQs.toString()}` : ""
  }`;

  window.location.href = routeFns.loginPage(returnTo);
};

apiAxios.interceptors.response.use((response) => {
  if (response.status === 401) {
    const hintedGuestUserId = response?.headers?.[guestUserLoginHint];

    if (hintedGuestUserId) {
      window.location.href = routeFns.guestUserLogin(
        hintedGuestUserId,
        `${window.location.pathname}${window.location.search}`,
      );
    } else if (window.location.pathname === "/login-failed") {
      logout();
    } else {
      redirectToLogin();
    }
  }
  return response;
});

apiAxios.interceptors.response.use((response) => {
  if (response.status === 451) {
    const patientId = response.data.message;
    if (patientId) {
      window.location.replace(
        routeFns.PatientSearchReasoningPage(
          patientId,
          window.location.pathname,
        ),
      );
    } else {
      window.location.replace(routeFns.home());
      renderErrorToast({
        message: "Please search for your patient and try again",
      });
    }
  }
  return response;
});

apiAxios.interceptors.response.use((response) => {
  checkVersionMismatch(response.headers["x-code-version"]);
  return response;
});

const useRawApiAxios = makeUseAxios({ axios: apiAxios, cache: false });

/**
 * Wraps an endpoint hook so a refusal body is narrowed out of `data` (see
 * `okResponseData`); a caller that wants the refusal reads `response`. Calling
 * `useRawApiAxios` directly bypasses that narrowing.
 */
function useApiAxios<TResponse, TBody = unknown, TError = unknown>(
  config: AxiosRequestConfig<TBody> | string,
  options?: Options,
): UseAxiosResult<TResponse, TBody, TError> {
  const [state, execute, cancel] = useRawApiAxios<TResponse, TBody, TError>(
    config,
    options,
  );

  // `state` only changes when a request starts or settles, and a caller may
  // use it as a dependency; rebuilding it every render would defeat that.
  const narrowedState = useMemo(
    () => ({ ...state, data: okResponseData(state.data, state.response) }),
    [state],
  );

  return [narrowedState, execute, cancel];
}

export const apiConfig = {
  pathway: {
    getPatientPathways: () => ({
      url: "/api/pathway/patient-pathways",
      method: "GET",
    }),
    getPatientDetentionPeriods: (rootPatientId: string) => ({
      url: `/api/pathway/patients/${rootPatientId}/detention-periods`,
      method: "GET",
    }),
    getDetentionPeriods: () => ({
      url: "/api/pathway/detention-periods",
      method: "GET",
    }),
    getFacets: () => ({
      url: "/api/pathway/facets",
      method: "GET",
    }),
    getPatientRows: (filters: PathwayPatientRowsQuery) => {
      // Repeated keys (hospitals=a&hospitals=b), matching tsoa's array @Query
      // binding — not axios's default bracketed serialization.
      const params = new URLSearchParams();
      (filters.hospitals ?? []).forEach((h) => {
        params.append("hospitals", h);
      });
      (filters.trusts ?? []).forEach((t) => {
        params.append("trusts", t);
      });
      if (filters.limit != null) params.set("limit", String(filters.limit));
      const qs = params.toString();
      return {
        url: `/api/pathway/patient-rows${qs ? `?${qs}` : ""}`,
        method: "GET",
      };
    },
    getCohortStats: (query: PathwayCohortQuery) => ({
      url: "/api/pathway/cohort-stats",
      method: "POST",
      data: query,
    }),
    getPatientTimelines: (rootPatientIds: string[]) => ({
      url: "/api/pathway/patient-timelines",
      method: "POST",
      data: { rootPatientIds },
    }),
  },
  connectivity: {
    getConnectivity: (params?: {
      from?: string;
      to?: string;
      window?: string;
    }) => {
      const search = new URLSearchParams();
      if (params?.from) search.set("from", params.from);
      if (params?.to) search.set("to", params.to);
      if (params?.window) search.set("window", params.window);
      const qs = search.toString();
      return {
        url: `/api/connectivity${qs ? `?${qs}` : ""}`,
        method: "GET",
      };
    },
  },
  patientList: {
    list: (
      offset?: number,
      limit?: number,
      sortBy?: PatientListSortBy,
      sortDirection?: PatientListSortDirection,
      patientSearchFilter?: string,
      mhaStatuses?: MhaStatus[],
      locationNames?: string[],
    ) => ({
      url: "/api/patient-list",
      method: "post",
      params: { offset, limit, sortBy, sortDirection },
      // The search filter is a patient name or NHS number: it travels in the
      // body so it never reaches an access log, browser history or a referrer
      // header.
      data: {
        ...(patientSearchFilter ? { patientSearchFilter } : {}),
        ...(mhaStatuses?.length ? { mhaStatuses } : {}),
        ...(locationNames?.length ? { locationNames } : {}),
      },
    }),
    add: (patientId: string) => ({
      url: `/api/patient-list/${patientId}`,
      method: "post",
    }),
    remove: (patientId: string) => ({
      url: `/api/patient-list/${patientId}`,
      method: "delete",
    }),
  },
  capacityState: {
    register: (careHomeIds?: string[], residentSearchFilter?: string) => ({
      url: `/api/capacity-state/register`,
      method: "post",
      params: { careHomeIds },
      paramsSerializer: {
        indexes: null, // Send repeated params without the [] suffix
      },
      // The resident filter is a patient name: it travels in the body so it never
      // reaches an access log, browser history or a referrer header.
      data: residentSearchFilter
        ? ({ residentSearchFilter } satisfies RegisterQueryBody)
        : {},
    }),
    history: (patientId: string) => ({
      url: `/api/capacity-state/${patientId}/history`,
      method: "get",
    }),
    addResident: (data: AddResidentRequest) => ({
      url: `/api/capacity-state/residents`,
      method: "post",
      data,
    }),
    removeResident: (patientId: string, data: RemoveResidentRequest) => ({
      url: `/api/capacity-state/${patientId}/remove`,
      method: "post",
      data,
    }),
    transferResident: (patientId: string, data: TransferResidentRequest) => ({
      url: `/api/capacity-state/${patientId}/transfer`,
      method: "post",
      data,
    }),
    recordStatus: (patientId: string, data: RecordCapacityStatusRequest) => ({
      url: `/api/capacity-state/${patientId}/record`,
      method: "post",
      data,
    }),
  },
  s132: {
    record: (patientId: string) => ({
      url: `/api/s132/${patientId}`,
      method: "get",
    }),
    recordAttempt: (patientId: string, data: RecordS132AttemptRequest) => ({
      url: `/api/s132/${patientId}/attempts`,
      method: "post",
      data,
    }),
    correctAttempt: (
      patientId: string,
      attemptId: string,
      data: CorrectS132AttemptRequest,
    ) => ({
      url: `/api/s132/${patientId}/attempts/${attemptId}/corrections`,
      method: "post",
      data,
    }),
    withdrawAttempt: (
      patientId: string,
      attemptId: string,
      data: WithdrawS132AttemptRequest,
    ) => ({
      url: `/api/s132/${patientId}/attempts/${attemptId}/withdrawal`,
      method: "post",
      data,
    }),
  },
  patientState: {
    csvReport: (
      request: PatientStateCsvRequest,
      statuses: MhaStatus[],
      expiryLessThan?: string,
      trustIds?: string[],
      hospitalIds?: string[],
    ) => ({
      url: `/api/patient-state/csv-report`,
      method: "post",
      data: request,
      params: {
        statuses,
        expiryLessThan,
        trustIds,
        hospitalIds,
      },
      paramsSerializer: {
        indexes: null, // Send repeated params without the [] suffix
      },
    }),
  },
  vision: {
    validateShareCode: (shareCode: string) => ({
      url: `/api/vision-guest/validate-share-code`,
      method: "post",
      data: { shareCode } satisfies ValidateShareCodeV1Request,
    }),
    accessRequest: (accessRequest: VisionAccessRequest) => ({
      url: `/api/vision-guest/access-request`,
      method: "post",
      data: accessRequest satisfies VisionAccessRequest,
    }),
    previewIncident: (incidentId: string) => ({
      url: `/api/vision-incident/${incidentId}`,
      method: "get",
    }),
    acceptIncident: (data: VisionAcceptIncidentRequest) => ({
      url: `/api/vision-incident/${data.incidentId}/accept`,
      method: "post",
      data,
    }),
    rejectIncident: (incidentId: string, reason: string) => ({
      url: `/api/vision-incident/${incidentId}/reject`,
      method: "post",
      data: { reason },
    }),
    synchroniseIncidentForFormContext: (formContextId: string) => ({
      url: `/api/vision-incident/form-context/${formContextId}/synchronise-data`,
      method: "post",
    }),
    getIncidentIdForFormContext: (formContextId: string) => ({
      url: `/api/vision-incident/form-context/${formContextId}/incident-id`,
      method: "get",
    }),
    requestPolicePresenceV2: (
      formContextId: string,
      recordPolicePresenceAction: RecordPolicePresenceV2Action,
    ) => ({
      url: `/api/vision-incident/${formContextId}/police-presence/v2`,
      method: "post",
      data: { recordPolicePresenceAction },
    }),
  },

  outcomeForm: {
    getForFormContext: (formContextId: string) => ({
      url: `/api/outcome-forms/form-context/${formContextId}`,
      method: "get",
    }),
    create: (request: CreateOutcomeFormRequest) => ({
      url: `/api/outcome-forms`,
      method: "put",
      data: request,
    }),
    claim: (formId: string, request: ClaimOutcomeFormRequest) => ({
      url: `/api/outcome-forms/${formId}/claim`,
      method: "post",
      data: request,
    }),
    complete: (formId: string) => ({
      url: `/api/outcome-forms/${formId}/complete`,
      method: "post",
    }),
  },

  patientDemographics: {
    getAlerts: (trustIds?: string[]) => ({
      url: "/api/patients/demographics/alerts",
      method: "get",
      params: trustIds && trustIds.length > 0 ? { trustIds } : undefined,
      paramsSerializer: {
        indexes: null, // Send repeated params without the [] suffix
      },
    }),
    getTrusts: () => ({
      url: "/api/patients/demographics/trusts",
      method: "get",
    }),
    clearAlertsForPatient: (patientId: string) => ({
      url: `/api/patients/demographics/alerts/patient/${patientId}/clear`,
      method: "post",
    }),
  },
  singleUseActionToken: {
    create: (id: UUID, body: CreateSingleUseActionTokenRequest) => ({
      url: `/api/single-use-action-token/${id}`,
      method: "put",
      data: body,
    }),
    get: (id: UUID) => ({
      url: `/api/single-use-action-token/${id}`,
      method: "get",
    }),
    getAll: () => ({
      url: "/api/single-use-action-token",
      method: "get",
    }),
    revoke: (id: UUID) => ({
      url: `/api/single-use-action-token/${id}/revoke`,
      method: "post",
    }),
    use: (id: UUID, csv: string, dryRun: boolean) => ({
      url: `/api/single-use-action-token/${id}/use?dryRun=${dryRun}`,
      method: "post",
      data: { csv },
    }),
    downloadTemplate: (id: UUID) => ({
      url: `/api/single-use-action-token/${id}/template.csv`,
      method: "get",
    }),
  },
  system: {
    generatePresignedUploadUrl: (body: GeneratePresignedUrlRequestBody) => ({
      url: `/api/system/generate-presigned-url`,
      method: "post",
      data: body,
    }),
    checkS12Approval: (gmcNumber: string) => ({
      url: `/api/system/s12-approval-check/${gmcNumber}`,
      method: "GET",
    }),
  },
  termsAndConditions: {
    getAll: () => ({
      url: "/api/system/terms-and-conditions",
      method: "get",
    }),
    get: (id: UUID) => ({
      url: `/api/system/terms-and-conditions/${id}`,
      method: "get",
    }),
    create: (id: UUID, req: CreateTermsAndConditionsRequest) => ({
      url: `/api/system/terms-and-conditions/${id}`,
      method: "put",
      data: req,
    }),
    accept: (req: AcceptTermsAndConditionsRequest) => ({
      url: `/api/user/accept-terms-and-conditions`,
      method: "post",
      data: req,
    }),
  },
  admissions: {
    delete: (itemId: string) => ({
      url: `/api/form-context/admission/${itemId}`,
      method: "delete",
    }),
    recordReasonForNotProceedingMedRec: (
      itemId: string,
      reason: string,
      reasonDescription?: string,
    ) => ({
      url: `/api/admission/${itemId}/record-reason-for-not-proceeding-med-rec`,
      method: "post",
      data: { reason, reasonDescription },
    }),
    recordReasonForNotProceedingAppForm: (
      itemId: string,
      reason: string,
      reasonDescription?: string,
    ) => ({
      url: `/api/admission/${itemId}/record-reason-for-not-proceeding-app-form`,
      method: "post",
      data: { reason, reasonDescription },
    }),
    reOpenAdmission: (
      itemId: string,
      reason: string,
      reasonDescription?: string,
    ) => ({
      url: `/api/admission/${itemId}/reopen`,
      method: "post",
      data: { reason, reasonDescription },
    }),
  },
  reports: {
    availableOrgsAndTeams: (params: { teamId?: string; orgId?: string }) => ({
      url: "/api/reports/available-orgs-and-teams",
      method: "GET",
      params,
    }),
    generateSignedFormsCsv: (params: SignedFormsCsvParams) => ({
      url: "/api/reports/signed-forms",
      method: "POST",
      data: params,
    }),
    getDashboardData: (params: ReportContextParams) => ({
      url: "/api/reports/dashboard-data",
      method: "POST",
      data: params,
    }),
    getNhsDashboardData: (organisationId: string) => ({
      url: `/api/reports/${organisationId}/nhs-dashboard-data/`,
      method: "GET",
    }),
    getSupersetDashboards: (organisationId: string) => ({
      url: `/api/reports/${organisationId}/superset-dashboards`,
      method: "GET",
    }),
    getSupersetDashboardData: (
      organisationId: string,
      supersetDashboardId: string,
    ) => ({
      url: `/api/reports/${organisationId}/superset-dashboard/${supersetDashboardId}`,
      method: "GET",
    }),
    exportSupersetDashboard: (
      organisationId: string,
      supersetDashboardId: string,
      format: "pdf" | "png",
      activeTabs?: string[],
      anchor?: string,
      dataMask?: Record<string, unknown>,
    ) => ({
      url: `/api/reports/${organisationId}/superset-dashboard/${supersetDashboardId}/export`,
      method: "POST",
      data: { format, activeTabs, anchor, dataMask },
    }),
    recordMhaDashboardView: (organisationId: string) => ({
      url: `/api/reports/${organisationId}/mha-dashboard/view`,
      method: "POST",
    }),
    getMhaDashboardLastViewed: (organisationId: string) => ({
      url: `/api/reports/${organisationId}/mha-dashboard/last-viewed`,
      method: "GET",
    }),
  },
  configuration: {
    getPdsConfiguration: () => ({
      url: "/api/configuration/pds",
      method: "GET",
    }),
    updatePdsConfiguration: (config: {
      apiKey: string;
      endpoint: string;
      jwtKid: string;
      privateKey: string;
    }) => ({
      url: "/api/configuration/pds",
      method: "PATCH",
      data: config,
    }),
    getRafConfiguration: () => ({
      url: "/api/configuration/raf",
      method: "GET",
    }),
    updateRafConfiguration: (config: {
      apiKey: string;
      endpoint: string;
      jwtKid: string;
      privateKey: string;
    }) => ({
      url: "/api/configuration/raf",
      method: "PATCH",
      data: config,
    }),
    getLocations: () => ({
      url: "/api/configuration/locations",
      method: "GET",
    }),
    getLocationsByIds: (ids: string[]) => ({
      url: "/api/configuration/locations/by-ids",
      method: "POST",
      data: { ids },
    }),
    addLocation: (location: Omit<ManagedLocation, "id" | "status">) => ({
      url: "/api/configuration/locations/add",
      method: "POST",
      data: location,
    }),
    updateLocation: (
      id: string,
      location: Omit<ManagedLocation, "id" | "status">,
    ) => ({
      url: `/api/configuration/locations/update/${id}`,
      method: "patch",
      data: location,
    }),
    deleteLocation: (id: UUID) => ({
      url: `/api/configuration/locations/delete/${id}`,
      method: "POST",
    }),
    updateLocalAuthorityPaymentRates: (
      id: string,
      rates: LocalAuthorityPaymentConfiguration,
    ) => ({
      url: `/api/configuration/locations/${id}/payment-rates`,
      method: "PATCH",
      data: rates,
    }),
    updateHospitalMhlAutoRouteConfig: (
      id: string,
      config: {
        teams: { teamId: string }[];
        guestEmails: string[];
      } | null,
    ) => ({
      url: `/api/configuration/locations/${id}/mhl-auto-route-config`,
      method: "PATCH",
      data: config,
    }),
    getApprovedEmailDomains: () => ({
      url: "/api/configuration/approved-email-domains",
      method: "GET",
    }),
    addApprovedEmailDomain: (pattern: string) => ({
      url: "/api/configuration/approved-email-domains",
      method: "POST",
      data: { pattern },
    }),
    removeApprovedEmailDomain: (pattern: string) => ({
      url: "/api/configuration/approved-email-domains/remove",
      method: "POST",
      data: { pattern },
    }),
    getEmailRedirects: () => ({
      url: "/api/configuration/email-redirects",
      method: "GET",
    }),
    getActiveEmailRedirects: () => ({
      url: "/api/configuration/email-redirects/active",
      method: "GET",
    }),
    addEmailRedirect: (
      sourceEmail: string,
      targetTeamId: string,
      message?: string,
    ) => ({
      url: "/api/configuration/email-redirects",
      method: "POST",
      data: { sourceEmail, targetTeamId, message },
    }),
    removeEmailRedirect: (id: string) => ({
      url: "/api/configuration/email-redirects/remove",
      method: "POST",
      data: { id },
    }),
    updateOrganisationConfiguration: (
      organisationId: string,
      configuration: Partial<OrganisationConfiguration>,
    ) => ({
      url: `/api/configuration/organisations/${organisationId}`,
      method: "POST",
      data: configuration,
    }),
    getS12ApprovalConfiguration: () => ({
      url: "/api/configuration/s12-approval",
      method: "GET",
    }),
    updateS12ApprovalConfiguration: (config: {
      endpoint: string;
      username: string;
      password: string;
    }) => ({
      url: "/api/configuration/s12-approval",
      method: "PATCH",
      data: config,
    }),
    getOdsConfiguration: () => ({
      url: "/api/configuration/ods",
      method: "GET",
    }),
    updateOdsConfiguration: (config: { endpoint: string; apiKey: string }) => ({
      url: "/api/configuration/ods",
      method: "PATCH",
      data: config,
    }),
    getXeroConfiguration: () => ({
      url: "/api/configuration/xero",
      method: "GET",
    }),
    updateXeroConfiguration: (config: Record<string, unknown>) => ({
      url: "/api/configuration/xero",
      method: "PATCH",
      data: config,
    }),
    getUserXeroContactId: (userId: string) => ({
      url: `/api/configuration/xero/user-contact/${encodeURIComponent(userId)}`,
      method: "GET",
    }),
    setUserXeroContactId: (userId: string, xeroContactId: string | null) => ({
      url: `/api/configuration/xero/user-contact/${encodeURIComponent(userId)}`,
      method: "PUT",
      data: { xeroContactId },
    }),
    searchXeroContacts: (query: string) => ({
      url: `/api/configuration/xero/contacts/search?q=${encodeURIComponent(query)}`,
      method: "GET",
    }),
  },
  ods: {
    getOrganisation: (odsCode: string) => ({
      url: `/api/ods/organisation/${encodeURIComponent(odsCode)}`,
      method: "GET",
      validateStatus: (status: number) =>
        (status >= 200 && status < 300) || status === 404,
    }),
    getLocalAuthorityByPostcode: (postcode: string) => ({
      url: `/api/ods/local-authority/by-postcode/${encodeURIComponent(postcode)}`,
      method: "GET",
      validateStatus: (status: number) =>
        (status >= 200 && status < 300) || status === 404,
    }),
    getLocalAuthorityByOdsCode: (odsCode: string) => ({
      url: `/api/ods/local-authority/by-ods-code/${encodeURIComponent(odsCode)}`,
      method: "GET",
      validateStatus: (status: number) =>
        (status >= 200 && status < 300) || status === 404,
    }),
    getIcbByGpOdsCode: (gpOdsCode: string) => ({
      url: `/api/ods/icb/by-gp/${encodeURIComponent(gpOdsCode)}`,
      method: "GET",
      validateStatus: (status: number) =>
        (status >= 200 && status < 300) || status === 404,
    }),
  },
  work: {
    create: (itemId: string, createRequest: CreateWorkItemRequest) => ({
      url: `/api/work/${itemId}`,
      method: "put",
      data: createRequest,
      validateStatus: (status: number) =>
        (status >= 200 && status < 300) || [401, 400].includes(status),
    }),
    retrieveWork: (
      mode: "caseload" | "archive" | "user-caseload",
      params: {
        limit?: number;
        offset?: number;
        patientSearchFilter?: string;
        selectedFormTemplateIds?: string[];
        selectedSenderNames?: string[];
        fromDate?: string;
        toDate?: string;
      },
    ) => ({
      url: `/api/work`,
      method: "post",
      params: { mode, ...params, patientSearchFilter: undefined },
      data: params.patientSearchFilter
        ? { patientSearchFilter: params.patientSearchFilter }
        : {},
    }),
    accept: (itemId: string, alsoClaim: boolean = true) => ({
      url: `/api/work/${itemId}/accept`,
      method: "post",
      params: { alsoClaim },
    }),
    reject: (
      itemId: string,
      rejectReason: string,
      rejectReasonDescription?: string,
    ) => ({
      url: `/api/work/${itemId}/reject`,
      method: "post",
      data: { rejectReason, rejectReasonDescription },
    }),

    claim: (itemId: string) => ({
      url: `/api/work/${itemId}/claim`,
      method: "post",
    }),
    unClaim: (itemId: string) => ({
      url: `/api/work/${itemId}/un-claim`,
      method: "post",
    }),
    archive: (itemId: string) => ({
      url: `/api/work/${itemId}/archive`,
      method: "post",
    }),
    unArchive: (itemId: string, alsoClaim: boolean = true) => ({
      url: `/api/work/${itemId}/un-archive`,
      method: "post",
      params: { alsoClaim },
    }),
    finalise: (itemId: string) => ({
      url: `/api/work/${itemId}/finalise`,
      method: "post",
    }),
    unfinalise: (itemId: string, req: UnfinaliseWorkItemRequest) => ({
      url: `/api/work/${itemId}/unfinalise`,
      method: "post",
      data: req,
    }),
    cancel: (itemId: string) => ({
      url: `/api/work/${itemId}/cancel`,
      method: "post",
    }),
    bulkArchive: (itemIds: string[]) => ({
      url: `/api/work/bulk-archive`,
      method: "post",
      data: { itemIds },
    }),
  },
  bulkDownload: {
    create: (
      filesToAdd: {
        fileName: string;
        formContextId: string;
      }[],
    ) => ({
      url: `/api/form-context/bulk-download`,
      method: "put",
      data: { filesToAdd },
    }),
    poll: (bulkDownloadId: string) => ({
      url: `/api/form-context/bulk-download/${bulkDownloadId}`,
      method: "get",
    }),
  },
  audit: {
    searchByFormContextId: (
      formContextId: string,
      params: {
        limit?: number;
        offset?: number;
        fromDate: string;
        toDate: string;
      },
    ) => ({
      url: `/api/audit/form-context/${formContextId}`,
      method: "get",
      params: params,
    }),

    searchByPatientId: (
      patientId: string,
      params: {
        limit?: number;
        offset?: number;
        fromDate: string;
        toDate: string;
      },
    ) => ({
      url: `/api/audit/patient/${patientId}`,
      method: "get",
      params: params,
    }),
    searchByResourceId: (
      resourceId: string,
      params: {
        limit?: number;
        offset?: number;
        fromDate: string;
        toDate: string;
      },
    ) => ({
      url: `/api/audit/resource/${resourceId}`,
      method: "get",
      params: params,
    }),
    search: (params: {
      limit?: number;
      offset?: number;
      fromDate: string;
      toDate: string;
      resourceId: string | null;
      type: AuditTrailEntryType[] | null;
      userId: string | null;
    }) => ({
      url: `/api/audit`,
      method: "get",
      params: {
        ...params,
        type: (params.type || [])?.length > 0 ? params.type?.join(",") : null,
      },
    }),
  },
  formTemplates: {
    searchAssignmentOptions: (search: FormPartAssignmentOptionsSearch) => ({
      url: "/api/form-templates/assignment-options",
      method: "post",
      data: search,
    }),
  },
  users: {
    search: (
      query: string,
      limit: number,
      offset: number,
      managingOrganisationId?: UUID,
    ) => ({
      url: "/api/user/search",
      method: "get",
      params: { query, limit, offset, managingOrganisationId },
    }),
    searchPersonalDataDownloadRecipients: (
      query: string,
      limit: number,
      offset: number,
    ) => ({
      url: "/api/user/personal-data-download-recipients",
      method: "get",
      params: { query, limit, offset },
    }),
    get: (userId: string) => ({ url: `/api/user/${userId}`, method: "get" }),
    me: (contextId: string | null) => ({
      url: "/api/user/me",
      method: "get",
      params: contextId ? { "context-id": contextId } : {},
    }),
    loggedInCheck: () => ({ url: "/api/user/logged-in-check", method: "get" }),
    update: (userId: string, query: UserUpdate) => ({
      url: `/api/user/${userId}/update`,
      method: "post",
      data: query,
    }),
    checkS12ApprovalForUser: (userId: string, gmcNumber: string) => ({
      url: `/api/user/${userId}/check-s12-approval`,
      method: "post",
      data: { gmcNumber },
    }),
    updateEmail: (userId: string, email: string) => ({
      url: `/api/user/${userId}/update-email`,
      method: "post",
      data: { newEmail: email.trim().toLowerCase() },
    }),
    convertGuestUser: (
      userId: string,
      request: GuestUserConversionRequest,
    ) => ({
      url: `/api/user/${userId}/convert-guest-user`,
      method: "post",
      data: request,
    }),
    convertFullUserToGuest: (userId: string) => ({
      url: `/api/user/${userId}/convert-full-user-to-guest`,
      method: "post",
    }),
    create: (userId: string, body: UserCreate) => ({
      url: `/api/user/${userId}`,
      method: "put",
      data: body,
    }),
    delete: (userId: string) => ({
      url: `/api/user/${userId}/delete`,
      method: "put",
    }),
    createIndependentContext: (userId: string) => ({
      url: `/api/user/${userId}/independent-context`,
      method: "post",
    }),
    deleteIndependentContext: (userId: string) => ({
      url: `/api/user/${userId}/independent-context/delete`,
      method: "post",
    }),
    getDeviceSessions: (userId: string) => ({
      url: `/api/user/${userId}/device-sessions`,
      method: "get",
    }),
    revokeDeviceSession: (userId: string, sessionId: string) => ({
      url: `/api/user/${userId}/device-sessions/${sessionId}/revoke`,
      method: "post",
    }),
    revokeAllDeviceSessions: (userId: string) => ({
      url: `/api/user/${userId}/device-sessions/revoke-all`,
      method: "post",
    }),
    getOrganisationConfiguration: (userId: string) => ({
      url: `/api/user/${userId}/organisation-configuration`,
      method: "get",
    }),
    setOrganisationConfiguration: (
      userId: string,
      organisationId: string,
      configuration: UserOrganisationConfiguration,
    ) => ({
      url: `/api/user/${userId}/organisation-configuration/${organisationId}`,
      method: "put",
      data: configuration,
    }),
    setSessionContext: (membership: Membership) => ({
      url: "/api/user/context",
      method: "post",
      data: membership,
    }),
    tryGuestUserLoginCode: (userId: string, code: string) => ({
      url: "/api/guests/login",
      method: "post",
      data: { userId, code },
      validateStatus: () => true,
    }),
    requestGuestUserLoginCode: (userId: string, forceSend?: boolean) => ({
      url: "/api/guests/request-code",
      method: "post",
      data: { userId },
      params: { forceSend },
      validateStatus: () => true,
    }),
    resetGuestUserLogins: (userId: string) => ({
      url: "/api/guests/admin/reset-logins",
      method: "post",
      data: { userId },
      validateStatus: () => true,
    }),
    getGuestSessionAccreditation: () => ({
      url: "/api/guests/session-accreditation",
      method: "get",
    }),
    submitGuestSessionAccreditation: (data: {
      fullName: string;
      gmcNumber: string;
      isSection12Approved: boolean;
      overrideApproval?: boolean;
      section12ExpiryDate?: string;
    }) => ({
      url: "/api/guests/session-accreditation",
      method: "put",
      data,
    }),
    lookupGmcNumber: (gmcNumber: string) => ({
      url: `/api/guests/session-accreditation/lookup/${encodeURIComponent(gmcNumber)}`,
      method: "get",
    }),
    ensureGuestUser: (email: string, name: string) => ({
      url: "/api/guests/ensure",
      method: "post",
      data: { email, name },
    }),
    triggerPasswordResetEmail: (userId: string) => ({
      url: `/api/user/${userId}/send-password-reset`,
      method: "post",
    }),
    bulkConfigure: (csvData: string, dryRun: boolean) => ({
      url: `/api/user/bulk-configure-users`,
      method: "post",
      data: { csv: csvData },
      params: { dryRun },
    }),
    bulkCreate: (
      csvData: string,
      dryRun: boolean,
      managingOrganisationId?: UUID,
    ) => {
      return {
        url: `/api/user/bulk-create`,
        method: "post",
        data: { csv: csvData },
        params: { dryRun, managingOrganisationId },
      };
    },
    allowSmsMfa: (userId: string) => ({
      url: `/api/user/${userId}/allow-sms-mfa`,
      method: "post",
    }),
    disallowSmsMfa: (userId: string) => ({
      url: `/api/user/${userId}/disallow-sms-mfa`,
      method: "post",
    }),
    enforceMfa: (userId: string) => ({
      url: `/api/user/${userId}/enforce-mfa`,
      method: "post",
    }),
    disableMfa: (userId: string) => ({
      url: `/api/user/${userId}/disable-mfa`,
      method: "post",
    }),
    resetMfa: (userId: string) => ({
      url: `/api/user/${userId}/reset-mfa`,
      method: "post",
    }),
    addFavouriteRecipient: (favourite: AddFavouriteRecipientRequest) => ({
      url: `/api/user/favourite-recipients`,
      method: "post",
      data: favourite,
    }),
    removeFavouriteRecipient: (
      dataControllerId: string,
      teamId: string,
      userId?: string,
    ) => ({
      url: `/api/user/favourite-recipients/delete`,
      method: "post",
      data: { dataControllerId, teamId, userId },
    }),
  },
  teams: {
    create: (teamId: string, createTeamRequest: CreateTeamRequest) => ({
      url: `/api/team/${teamId}`,
      method: "put",
      data: createTeamRequest,
    }),
    update: (teamId: string, createTeamRequest: UpdateTeamRequest) => ({
      url: `/api/team/${teamId}`,
      method: "post",
      data: createTeamRequest,
    }),
    delete: (teamId: string, createTeamRequest: DeleteTeamRequest) => ({
      url: `/api/team/${teamId}/delete`,
      method: "put",
      data: createTeamRequest,
    }),
    mergePreview: (primaryTeamId: string, secondaryTeamId: string) => ({
      url: `/api/team/${primaryTeamId}/merge-preview`,
      method: "get",
      params: { secondaryTeamId },
    }),
    merge: (primaryTeamId: string, body: MergeTeamRequest) => ({
      url: `/api/team/${primaryTeamId}/merge`,
      method: "post",
      data: body,
    }),
    get: (teamId: string) => ({ url: `/api/team/${teamId}`, method: "get" }),
    search: (
      query: string,
      limit: number = 10,
      offset: number = 0,
      ancestorOrganisationId?: string,
    ) => {
      return {
        url: `/api/team/search`,
        method: "get",
        params: {
          query,
          limit,
          offset,
          ancestorOrganisationId,
        },
      };
    },
    addMember: (teamId: string, req: AddTeamMemberRequest) => ({
      url: `/api/team/${teamId}/members/add`,
      method: "post",
      data: req,
    }),
    removeMember: (teamId: string, req: RemoveTeamMemberRequest) => ({
      url: `/api/team/${teamId}/members/remove`,
      method: "post",
      data: req,
    }),
    getLocalAuthorityMappings: (teamId: string) => ({
      url: `/api/team/${teamId}/local-authority-mappings`,
      method: "get",
    }),
    getCareHomeGrants: (teamId: string) => ({
      url: `/api/team/${teamId}/care-home-grants`,
      method: "get",
    }),
    setCareHomeGrants: (teamId: string, grants: TeamCareHomeGrant[]) => ({
      url: `/api/team/${teamId}/care-home-grants`,
      method: "put",
      data: { grants } satisfies SetCareHomeGrantsRequest,
    }),
    setLocalAuthorityMappings: (
      teamId: string,
      localAuthorityIds: string[],
    ) => ({
      url: `/api/team/${teamId}/local-authority-mappings`,
      method: "put",
      data: { localAuthorityIds },
    }),
  },
  organisations: {
    addUserToOrganisation: (organisationId: string, userId: string) => ({
      url: `/api/organisation/${organisationId}/add-user/${userId}`,
      method: "post",
    }),
    downloadAllUserEmails: (organisationId: string) => ({
      url: `/api/organisation/${organisationId}/download-all-user-emails`,
      method: "get",
    }),
    bulkUploadUsersToOrganisation: (
      organisationId: string,
      emails: string[],
      dryRun?: boolean,
    ) => ({
      url: `/api/organisation/${organisationId}/bulk-upload-users`,
      method: "post",
      data: emails,
      params: { dryRun },
    }),
    removeUserFromOrganisation: (organisationId: string, userId: string) => ({
      url: `/api/organisation/${organisationId}/remove-user/${userId}`,
      method: "post",
    }),
    addMember: (orgId: string, req: AddOrganisationMemberRequest) => ({
      url: `/api/organisation/${orgId}/members/add`,
      method: "post",
      data: req,
    }),
    removeMember: (orgId: string, req: RemoveOrganisationMemberRequest) => ({
      url: `/api/organisation/${orgId}/members/remove`,
      method: "post",
      data: req,
    }),
    create: (
      organisationId: string,
      createOrgReq: CreateOrganisationRequest,
    ) => ({
      url: `/api/organisation/${organisationId}`,
      method: "put",
      data: createOrgReq,
    }),
    update: (
      organisationId: string,
      updateOrgReq: UpdateOrganisationRequest,
    ) => ({
      url: `/api/organisation/${organisationId}`,
      method: "post",
      data: updateOrgReq,
    }),
    delete: (
      organisationId: string,
      updateOrgReq: DeleteOrganisationRequest,
    ) => ({
      url: `/api/organisation/${organisationId}/delete`,
      method: "put",
      data: updateOrgReq,
    }),
    get: (organisationId: string) => ({
      url: `/api/organisation/${organisationId}`,
      method: "get",
    }),
    getDrFinderAssessingOrganisations: (localAuthorityId: string) => ({
      url: `/api/organisation/organising-local-authority/${localAuthorityId}`,
      method: "get",
    }),
    setDrFinderAssessingOrganisations: (
      localAuthorityId: string,
      req: SetDrFinderAssessingOrganisationsRequest,
    ) => ({
      url: `/api/organisation/organising-local-authority/${localAuthorityId}`,
      method: "put",
      data: req,
    }),
    search: (
      query: string,
      limit: number = 10,
      offset: number = 0,
      role?: string,
    ) => {
      return {
        url: `/api/organisation/search`,
        method: "get",
        params: {
          query,
          limit,
          offset,
          role,
        },
      };
    },
  },
  dataSharingPartnership: {
    getAll: () => ({
      url: "/api/data-sharing-partnership",
      method: "GET",
    }),

    create: (
      id: DataSharingPartnership["id"],
      name: DataSharingPartnership["name"],
    ) => ({
      url: `/api/data-sharing-partnership/${id}`,
      method: "put",
      data: { name },
    }),

    delete: (id: DataSharingPartnership["id"]) => ({
      url: `/api/data-sharing-partnership/${id}/delete`,
      method: "post",
    }),

    addDataController: (
      dataPartnershipId: DataSharingPartnership["id"],
      dataControllerId: Organisation["id"],
    ) => ({
      url: `/api/data-sharing-partnership/${dataPartnershipId}/add-data-controller/${dataControllerId}`,
      method: "put",
    }),

    removeDataController: (
      dataPartnershipId: DataSharingPartnership["id"],
      dataControllerId: Organisation["id"],
    ) => ({
      url: `/api/data-sharing-partnership/${dataPartnershipId}/remove-data-controller/${dataControllerId}`,
      method: "post",
    }),
  },
  notifications: {
    get: (
      query: {
        limit?: number;
        offset?: number;
      } = {},
    ) => ({ url: "/api/notifications", method: "get", params: query }),
    markAsViewed: (notificationIds: string[]) => ({
      url: `/api/notifications/viewed`,
      method: "post",
      data: { notificationIds },
    }),
  },
  patients: {
    search: (query: PatientIndexQuery) => ({
      url: "/api/patients/search",
      method: "post",
      data: query,
    }),

    searchV2: (searchId: UUID, query: PatientSearchRequestV2) => ({
      url: `/api/patients/search/v2/${searchId}`,
      method: "post",
      data: query,
    }),

    addSearchV2Reasoning: (
      resultId: UUID,
      body: SaveSearchReasoningV2Request,
    ) => ({
      url: `/api/patients/search/v2/${resultId}/save-reasoning`,
      method: "post",
      data: body,
    }),

    markAlertAsSeen(patientId: string, alertId: string) {
      return {
        url: `/api/patients/${patientId}/mark-alert-as-seen`,
        method: "post",
        data: { alertId },
      };
    },

    getNhsFlags: (patientId: string) => ({
      url: `/api/patients/${patientId}/nhs-flags`,
      method: "get",
    }),

    saveSearchReasoningToSession: (query: SearchReasoning) => ({
      url: "/api/patients/save-search-reasoning-for-patient-on-session",
      method: "post",
      data: query,
    }),
    merge: (query: PatientMergeInfo) => ({
      url: "/api/patients/merge",
      method: "post",
      data: query,
    }),
    unmerge: (
      query: PatientUnmergeIds & {
        formContextIds?: string[] | undefined;
        reason: string;
        reasonDescription?: string;
      },
    ) => ({
      url: "/api/patients/unmerge",
      method: "post",
      data: query,
    }),
    get: (patientId: string) => ({
      url: `/api/patients/${patientId}`,
      method: "get",
    }),
    getExternalPatientLinks: (
      patientId: string,
      query: { patientIdsToTreatAsRootPatients: string[] },
    ) => ({
      url: `/api/patients/${patientId}/external-patient-links`,
      params: query,
      paramsSerializer: {
        indexes: null, // Send repeated params without the [] suffix
      },
      method: "get",
    }),
    getTimeline: (
      patientId: string,
      query: {
        limit?: number;
        offset?: number;
      },
    ) => ({
      url: `/api/patients/${patientId}/timeline`,
      method: "get",
      params: query,
    }),
    getMhaState: (
      patientId: string,
      query: {
        limit: number;
        offset: number;
        // Read-only unmerge preview: treat these patients as merge-tree roots
        // to see each patient's status as it would be after an unmerge.
        patientIdsToTreatAsRootPatients?: string[];
      },
    ) => ({
      url: `/api/patients/${patientId}/mha-state`,
      method: "get",
      params: query,
      paramsSerializer: {
        indexes: null, // Send repeated params without the [] suffix
      },
    }),
    getMhaDashboardData: (params: MhaDashboardDataParams) => ({
      url: "/api/patients/mha-dashboard-data",
      method: "post",
      params: { ...params, patientSearchFilter: undefined },
      paramsSerializer: {
        indexes: null, // Send repeated params without the [] suffix
      },
      data: params.patientSearchFilter
        ? { patientSearchFilter: params.patientSearchFilter }
        : {},
    }),
    getMhaDashboardTrusts: () => ({
      url: "/api/patients/mha-dashboard-trusts",
      method: "get",
    }),
    create: (patientId: string, createPatient: CreatePatientRequest) => ({
      url: `/api/patients/${patientId}`,
      method: "put",
      data: createPatient,
    }),
    createFromExternalId: (request: CreatePatientFromExternalIdRequest) => ({
      url: `/api/patients/createFromExternalId`,
      method: "post",
      data: request,

      // Pass through all errors to the calling code to handle as we want
      // to diplay them (e.g. 502) to the user rather than throwing an error
      validateStatus: () => true,
    }),
    createExternalPatientDemographicsPullEvent: (
      patientId: string,
      request: CreateExternalPatientDemograhicsPullEventRequest,
    ) => ({
      url: `/api/patients/${patientId}/external-patient-link/pull-demographics`,
      method: "post",
      data: request,
    }),
    updateExternalPatientDemographicsPullEvent: (
      patientId: string,
      pullEventId: string,
      request: UpdateExternalPatientDemograhicsPullEventRequest,
      choiceDifferences?: ChoiceDifferencesEntry[],
    ) => {
      return {
        url: `/api/patients/${patientId}/external-patient-link/pull-demographics/${pullEventId}`,
        method: "patch",
        data: { ...request, choiceDifferences },
      };
    },

    getExternalPatientDemographicsPullEvent: (
      patientId: string,
      pullEventId: string,
    ) => ({
      url: `/api/patients/${patientId}/external-patient-link/pull-demographics/${pullEventId}`,
      method: "get",
    }),
    update: (patientId: string, updatePatient: UpdatePatientRequest) => ({
      url: `/api/patients/${patientId}`,
      method: "patch",
      data: updatePatient,
    }),
    updatePatientMhaStateWithManualEvent: (
      patientId: string,
      request: UpdatePatientStateWithManualEventRequest,
    ) => ({
      url: `/api/patients/${patientId}/mha-state`,
      method: "post",
      data: request,
    }),
    getPatientMhaState: (patientId: string) => ({
      url: `/api/patients/${patientId}/mha-state`,
      method: "get",
    }),
    getSection136PlaceOfSafety: (patientId: string) => ({
      url: `/api/patients/${patientId}/section-136-place-of-safety`,
      method: "get",
    }),
  },
  forms: {
    createSoadAssessmentContext: (
      formContextId: string,
      patientId: string,
      requestId: string,
    ) => ({
      url: `/api/form-context/${formContextId}`,
      method: "put",
      data: { type: "soad-assessment", patientId, requestId },
    }),
    createAdmissionsContext: (formContextId: string, patientId: string) => ({
      url: `/api/form-context/${formContextId}`,
      method: "put",
      data: { type: "admission", patientId },
    }),
    uploadPdfToExternalSystem: (
      formContextId: string,
      formId: string,
      externalPatientLinkId: string,
      filename: string,
      title: string,
      description: string,
      finalRevision: boolean,
      linkedFormIds?: string[],
    ) => ({
      url: `/api/form-context/${formContextId}/form/${formId}/uploadPdfToExternalSystem`,
      method: "post",
      data: {
        externalPatientLinkId,
        filename,
        title,
        description,
        finalRevision,
        linkedFormIds,
      },
    }),
    getExternalPatientFormPushEvent: (
      formContextId: string,
      formId: string,
    ) => ({
      url: `/api/form-context/${formContextId}/form/${formId}/externalPatientFormPushEvent`,
      method: "get",
    }),
    uploadForm: (
      formContextId: string,
      formId: string,
      data: UploadFormRequest,
    ) => ({
      url: `/api/form-context/${formContextId}/form/${formId}/upload`,
      method: "put",
      data: data,
    }),
    uploadFormPreview: (
      formContextId: string,
      formId: string,
      data: UploadFormRequest,
    ) => ({
      url: `/api/form-context/${formContextId}/form/${formId}/upload-preview`,
      method: "post",
      data: data,
    }),
    sign: (formId: string, createFormFromDraft: SignDraftRequest) => ({
      url: `/api/forms/${formId}/sign`,
      method: "put",
      data: createFormFromDraft,
    }),
    requestAmend: (formId: string, amendRequest: RequestAmendRequest) => ({
      url: `/api/forms/${formId}/request-amend`,
      method: "post",
      data: amendRequest,
    }),
    requestDeMinimis: (
      formId: string,
      body: {
        targetUserId: string;
        targetFormPart: number;
        errorType: string;
        note: string;
        selectedFields?: string[];
      },
    ) => ({
      url: `/api/forms/${formId}/request-de-minimis`,
      method: "post",
      data: body,
    }),
    acceptDeMinimis: (formId: string, body: { fileNoteId: number }) => ({
      url: `/api/forms/${formId}/accept-de-minimis`,
      method: "post",
      data: body,
    }),
    completeDeMinimis: (
      formId: string,
      body: {
        fileNoteId: number;
        response: string;
        signature: string;
        signatoryName: string;
      },
    ) => ({
      url: `/api/forms/${formId}/complete-de-minimis`,
      method: "post",
      data: body,
    }),
    rejectDeMinimis: (
      formId: string,
      body: { fileNoteId: number; rejectedReason: string },
    ) => ({
      url: `/api/forms/${formId}/reject-de-minimis`,
      method: "post",
      data: body,
    }),
    cancelDeMinimis: (
      formId: string,
      body: { fileNoteId: number; reason: string },
    ) => ({
      url: `/api/forms/${formId}/cancel-de-minimis`,
      method: "post",
      data: body,
    }),
    // Redo admission forms (non-s15). request-redo returns 204/no body;
    // initiate-redo and accept-redo return { newFormId }.
    requestRedo: (formId: string, body: RequestRedoRequest) => ({
      url: `/api/forms/${formId}/request-redo`,
      method: "post",
      data: body,
    }),
    initiateRedo: (formId: string, body: RequestRedoRequest) => ({
      url: `/api/forms/${formId}/initiate-redo`,
      method: "post",
      data: body,
    }),
    retractRedo: (formId: string, body: RedoReasonRequest) => ({
      url: `/api/forms/${formId}/retract-redo`,
      method: "post",
      data: body,
    }),
    acceptRedo: (formId: string) => ({
      url: `/api/forms/${formId}/accept-redo`,
      method: "post",
      data: {},
    }),
    declineRedo: (formId: string, body: RedoReasonRequest) => ({
      url: `/api/forms/${formId}/decline-redo`,
      method: "post",
      data: body,
    }),
    cancelRedo: (formId: string, body: RedoReasonRequest) => ({
      url: `/api/forms/${formId}/cancel-redo`,
      method: "post",
      data: body,
    }),
    addFileNote: (formId: string, fileNote: AddFileNote) => ({
      url: `/api/forms/${formId}/add-file-note`,
      method: "post",
      data: fileNote,
    }),
    deleteFileNotes: (
      formId: string,
      fileNoteIds: { fileNoteIds: string },
    ) => ({
      url: `/api/forms/${formId}/delete-file-notes`,
      method: "post",
      data: fileNoteIds,
    }),
    getFileNotes: (formId: string) => ({
      url: `/api/forms/${formId}/get-file-notes`,
      method: "get",
    }),
    delete: (formId: string) => ({
      url: `/api/forms/${formId}`,
      method: "delete",
    }),
    createFromAssignment: (
      formId: string,
      createFormFromDraft: CreateFormRequest,
    ) => ({
      url: `/api/forms/${formId}`,
      method: "put",
      data: createFormFromDraft,
    }),
    get: (formId?: string) => ({
      url: `/api/forms/${formId}`,
      method: "get",
    }),
    getFormContext: (formContextId?: string) => ({
      url: `/api/form-context/${formContextId}`,
      method: "get",
    }),
    getPdf: (
      formId?: string,
      includeAllLinkedForms?: boolean,
      includeLinkedForms?: string[],
      version?: number,
    ) => ({
      url: `/api/forms/${formId}/pdf`,
      method: "get",
      params: { includeAllLinkedForms, includeLinkedForms, version },
      paramsSerializer: {
        indexes: null, // Send repeated params without the [] suffix
      },
    }),
    getRioUploadData: (
      formContextId: string,
      formId: string,
      isBulkUpload?: boolean,
      linkedFormIds?: string[],
    ) => ({
      url: `/api/form-context/${formContextId}/form/${formId}/get-rio-upload-data`,
      method: "get",
      params: { isBulkUpload, linkedFormIds },
      paramsSerializer: {
        indexes: null, // Send repeated params without the [] suffix
      },
    }),
    giveMyselfAccess: (formContextId: string) => ({
      url: `/api/form-context/${formContextId}/giveMyselfAccess`,
      method: "post",
    }),
    updateOfficialReferenceId: (
      formContextId: string,
      officialReferenceId: UpdateFormContextOfficialReferenceIdRequest["officialReferenceId"],
    ) => ({
      url: `/api/form-context/${formContextId}/officialReferenceId`,
      method: "post",
      data: {
        officialReferenceId,
      } satisfies UpdateFormContextOfficialReferenceIdRequest,
    }),
    deleteUploadedForm: (formId: string) => ({
      url: `/api/forms/${formId}/delete-uploaded-form`,
      method: "delete",
    }),
    updateAssessmentLocation: (
      formContextId: string,
      body: UpdateFormContextAssessmentLocationRequest,
    ) => ({
      url: `/api/form-context/${formContextId}/assessmentLocation`,
      method: "post",
      data: body,
    }),
  },
  soadUserTeamOrg: {
    create: (request: CreateSoadRequest) => ({
      url: `/api/soad/create`,
      method: "put",
      data: request,
    }),
    getSoads: (cqcOrganisationId: string) => ({
      url: `/api/soad/${cqcOrganisationId}/soads`,
      method: "get",
    }),
    delete: (request: DeleteSoadContextRequest) => ({
      url: `/api/soad/delete-context`,
      method: "post",
      data: request,
    }),
  },
  soad: {
    getPdf: (formContextId: string, formId: string, version?: number) => ({
      url: `/api/form-context/${formContextId}/soad-assessment/${formId}/pdf`,
      method: "get",
      params: { version },
    }),
    addStatutoryCertificate: (
      formContextId: string,
      certificateType: string,
    ) => ({
      url: `/api/form-context/${formContextId}/statutory-certificate/${certificateType}`,
      method: "post",
    }),
    deleteStatutoryCertificate: (
      formContextId: string,
      certificateType: string,
    ) => ({
      url: `/api/form-context/${formContextId}/statutory-certificate/${certificateType}`,
      method: "delete",
    }),
    signAndShareSoadAssessment: (
      formContextId: string,
      request: SignSoadAssessmentRequest,
    ) => ({
      url: `/api/form-context/${formContextId}/sign-soad-assessment`,
      method: "post",
      data: request,
    }),
    startAmendment: (
      formContextId: string,
      reasoning: { reason: string; reasonDescription?: string },
    ) => ({
      url: `/api/form-context/${formContextId}/soad-assessment/start-amendment`,
      method: "post",
      data: { reasoning },
    }),
    cancelAmendment: (formContextId: string) => ({
      url: `/api/form-context/${formContextId}/soad-assessment/cancel-amendment`,
      method: "post",
    }),
    delete: (formContextId: string) => ({
      url: `/api/form-context/${formContextId}/soad-assessment`,
      method: "delete",
    }),
  },
  drafts: {
    create: (formDraftId: string, createDraft: CreateDraft) => ({
      url: `/api/formdrafts/${formDraftId}`,
      method: "put",
      data: createDraft,
    }),
    getAll: (patientId?: string) => ({
      url: "/api/formdrafts",
      method: "get",
      params: { patientId },
    }),
    get: (formDraftId: string) => ({
      url: `/api/formdrafts/${formDraftId}`,
      method: "get",
    }),
    getPdf: (formDraftId: string, review: boolean) => ({
      url: `/api/formdrafts/${formDraftId}/pdf`,
      method: "get",
      params: review ? { review: "true" } : {},
    }),
    delete: (formDraftId: string) => ({
      url: `/api/formdrafts/${formDraftId}`,
      method: "delete",
    }),
    updateData: (formDraftId: string, data: any) => ({
      url: `/api/formdrafts/${formDraftId}/data`,
      method: "post",
      data: data,
    }),
  },
  admin: {
    getGuestUserLoginCode: (userId: string) => ({
      url: `/api/guests/admin/request-code`,
      method: "post",
      data: { userId },
    }),
    getDataPermissionInfo: (
      resourceType: "patient" | "form-context",
      resourceId: string,
    ) => ({
      url: `/api/system/admin/data-permission-info`,
      method: "post",
      data: { resourceType, resourceId },
    }),
    deleteDataPermission: (
      resourceType: "patient" | "form-context",
      resourceId: string,
      dataControllerId: string,
      ticketNumber: string,
    ) => ({
      url: `/api/system/delete-data-permission`,
      method: "post",
      data: { resourceType, resourceId, dataControllerId, ticketNumber },
    }),
    deleteWorkItem: (
      workItemId: string,
      reason: string,
      ticketNumber: string,
    ) => ({
      url: `/api/work/admin/delete`,
      method: "post",
      data: { workItemId, reason, ticketNumber },
    }),
    getPatientDetails: (data: AdminPatientDetailsRequest) => ({
      url: `/api/system/admin/patient-details`,
      method: "post",
      data,
    }),
  },
  rio: {
    instance: {
      get: (rioInstanceId: string) => ({
        url: `/api/epr/rio/instances/${rioInstanceId}`,
        method: "get",
      }),
      create: (rioInstanceId: string, data: UpsertRioInstanceRequest) => ({
        url: `/api/epr/rio/instances/${rioInstanceId}`,
        method: "put",
        data: data,
      }),
      update: (rioInstanceId: string, data: UpsertRioInstanceRequest) => ({
        url: `/api/epr/rio/instances/${rioInstanceId}`,
        method: "post",
        data: data,
      }),
      search: (query: string, limit: number = 10, offset: number = 0) => ({
        url: `/api/epr/rio/search/instances`,
        method: "get",
        params: {
          query,
          limit,
          offset,
        },
      }),
      delete: (rioInstanceId: string) => ({
        url: `/api/epr/rio/instances/${rioInstanceId}`,
        method: "delete",
      }),
      testConnection: (rioInstanceId: string) => ({
        url: `/api/epr/rio/instances/${rioInstanceId}/connection-test`,
        method: "post",
      }),
    },
    clickThrough: {
      createAndClaimToken: (
        data: CreateAndClaimOneTimeTokenForRioClickThroughInstanceRequest,
      ) => ({
        url: `/api/epr/rio/createAndClaimToken`,
        method: "post",
        data: data,
      }),
      redeemToken: (
        data: RedeemOneTimeTokenForRioClickThroughInstanceRequest,
      ) => ({
        url: `/api/epr/rio/redeemToken`,
        method: "post",
        data: data,
      }),
    },
  },
  supersetDashboards: {
    get: (dashboardId: string) => ({
      url: `/api/superset-dashboards/${dashboardId}`,
      method: "get",
    }),
    getDataGlossary: (dashboardId: string) => ({
      url: `/api/superset-dashboards/${dashboardId}/data-glossary`,
      method: "get",
    }),
    create: (dashboardId: string, data: UpsertSupersetDashboardRequest) => ({
      url: `/api/superset-dashboards/${dashboardId}`,
      method: "put",
      data: data,
    }),
    update: (dashboardId: string, data: UpsertSupersetDashboardRequest) => ({
      url: `/api/superset-dashboards/${dashboardId}`,
      method: "post",
      data: data,
    }),
    search: (query: string, limit: number = 10, offset: number = 0) => ({
      url: `/api/superset-dashboards/search`,
      method: "get",
      params: { query, limit, offset },
    }),
    delete: (dashboardId: string) => ({
      url: `/api/superset-dashboards/${dashboardId}`,
      method: "delete",
    }),
  },
  tpp: {
    instance: {
      get: (tppInstanceId: string) => ({
        url: `/api/epr/tpp/instances/${tppInstanceId}`,
        method: "get",
      }),
      create: (tppInstanceId: string, data: UpsertTppInstanceRequest) => ({
        url: `/api/epr/tpp/instances/${tppInstanceId}`,
        method: "put",
        data: data,
      }),
      update: (tppInstanceId: string, data: UpsertTppInstanceRequest) => ({
        url: `/api/epr/tpp/instances/${tppInstanceId}`,
        method: "post",
        data: data,
      }),
      search: (query: string, limit: number = 10, offset: number = 0) => ({
        url: `/api/epr/tpp/search/instances`,
        method: "get",
        params: { query, limit, offset },
      }),
      delete: (tppInstanceId: string) => ({
        url: `/api/epr/tpp/instances/${tppInstanceId}`,
        method: "delete",
      }),
    },
    clickThrough: {
      redeemToken: (
        data: RedeemOneTimeTokenForTppClickThroughInstanceRequest,
      ) => ({
        url: `/api/epr/tpp/redeemToken`,
        method: "post",
        data: data,
      }),
    },
  },
  generic: {
    instance: {
      get: (genericInstanceId: string) => ({
        url: `/api/epr/generic/instances/${genericInstanceId}`,
        method: "get",
      }),
      create: (
        genericInstanceId: string,
        data: UpsertGenericInstanceRequest,
      ) => ({
        url: `/api/epr/generic/instances/${genericInstanceId}`,
        method: "put",
        data: data,
      }),
      update: (
        genericInstanceId: string,
        data: UpsertGenericInstanceRequest,
      ) => ({
        url: `/api/epr/generic/instances/${genericInstanceId}`,
        method: "post",
        data: data,
      }),
      search: (query: string, limit: number = 10, offset: number = 0) => ({
        url: `/api/epr/generic/search/instances`,
        method: "get",
        params: { query, limit, offset },
      }),
      delete: (genericInstanceId: string) => ({
        url: `/api/epr/generic/instances/${genericInstanceId}`,
        method: "delete",
      }),
    },
    clickThrough: {
      redeemToken: (
        data: RedeemOneTimeTokenForGenericClickThroughInstanceRequest,
      ) => ({
        url: `/api/epr/generic/redeemToken`,
        method: "post",
        data: data,
      }),
    },
  },
  eprClickThrough: {
    peek: (source: string, token: string) => ({
      url: `/api/epr/clickthrough/peek`,
      method: "get",
      params: { source, token },
    }),
    links: {
      search: (query: string, limit: number = 10, offset: number = 0) => ({
        url: `/api/epr/clickthrough/links/search`,
        method: "get",
        params: { query, limit, offset },
      }),
      delete: (id: string) => ({
        url: `/api/epr/clickthrough/links/${id}`,
        method: "delete",
      }),
    },
  },
  templates: {
    compileTemplate: (template: string, templateData: TemplateData) => ({
      url: "/api/templates/compile",
      method: "post",
      data: { template, templateData },
    }),
  },
  placeOfSafety: {
    search: (query: string, limit: number, offset: number) => ({
      url: "/api/place-of-safety/search",
      method: "get",
      params: { query, limit, offset },
    }),
    delete: (id: string) => ({
      url: `/api/place-of-safety/${id}/delete`,
      method: "post",
    }),
    create: (id: string, payload: CreatePlaceOfSafetyRequest) => ({
      url: `/api/place-of-safety/${id}`,
      method: "put",
      data: payload,
    }),
    update: (id: string, payload: UpdatePlaceOfSafetyRequest) => ({
      url: `/api/place-of-safety/${id}`,
      method: "post",
      data: payload,
    }),
  },
  trust: {
    getAll: () => ({
      url: "/api/trust",
      method: "get",
    }),
    create: (trustId: string, data: CreateTrustRequest) => ({
      url: `/api/trust/${trustId}`,
      method: "put",
      data,
    }),
    update: (id: string, data: UpdateTrustRequest) => ({
      url: `/api/trust/${id}`,
      method: "post",
      data,
    }),
    delete: (id: string) => ({
      url: `/api/trust/${id}/delete`,
      method: "post",
    }),
    get: (id: string) => ({
      url: `/api/trust/${id}`,
      method: "get",
    }),
    search: (query: string, limit: number, offset: number) => ({
      url: "/api/trust/search",
      method: "get",
      params: { query, limit, offset },
    }),
  },
  mentalHealthCareProvider: {
    getAll: () => ({
      url: "/api/mental-health-care-provider",
      method: "get",
      transformResponse: parseMentalHealthCareProviderGetAll,
    }),
    search: (query: string, limit: number, offset: number) => ({
      url: "/api/mental-health-care-provider/search",
      method: "get",
      params: { query, limit, offset },
    }),
    create: (id: string, data: CreateMentalHealthCareProviderRequest) => ({
      url: `/api/mental-health-care-provider/${id}`,
      method: "put",
      data,
    }),
    update: (id: string, data: UpdateMentalHealthCareProviderRequest) => ({
      url: `/api/mental-health-care-provider/${id}`,
      method: "post",
      data,
    }),
    delete: (id: string) => ({
      url: `/api/mental-health-care-provider/${id}/delete`,
      method: "post",
    }),
  },
  londonCareRecord: {
    getLondonCareRecordConfiguration: () => ({
      url: "/api/london-care-record/instances",
      method: "GET",
    }),
    create: (
      londonCareRecordId: string,
      data: CreateLondonCareRecordInstanceRequest,
    ) => ({
      url: `/api/london-care-record/instances/${londonCareRecordId}`,
      method: "put",
      data,
    }),
    update: (
      id: string,
      data: Partial<CreateLondonCareRecordInstanceRequest>,
    ) => ({
      url: `/api/london-care-record/instances/${id}`,
      method: "patch",
      data,
    }),
    delete: (id: string) => ({
      url: `/api/london-care-record/instances/${id}/delete`,
      method: "post",
    }),
  },
  fhirApiClients: {
    getAll: () => ({
      url: "/api/fhir-api-clients",
      method: "GET",
    }),
    getById: (id: string) => ({
      url: `/api/fhir-api-clients/${id}`,
      method: "GET",
    }),
    create: (data: {
      name: string;
      description?: string | null;
      publicKeyJwks: Record<string, unknown>;
      allowedOrganisationIds: string[];
      allowedScopes: string[];
      retentionMonths?: number;
    }) => ({
      url: "/api/fhir-api-clients",
      method: "POST",
      data,
    }),
    update: (
      id: string,
      data: {
        name?: string;
        description?: string | null;
        publicKeyJwks?: Record<string, unknown>;
        allowedOrganisationIds?: string[];
        allowedScopes?: string[];
        retentionMonths?: number;
        isActive?: boolean;
      },
    ) => ({
      url: `/api/fhir-api-clients/${id}`,
      method: "PATCH",
      data,
    }),
    delete: (id: string) => ({
      url: `/api/fhir-api-clients/${id}`,
      method: "DELETE",
    }),
    getAvailableDataControllers: () => ({
      url: "/api/fhir-api-clients/available-data-controllers",
      method: "GET",
    }),
  },
  health: {
    version: () => ({
      url: "/api/health/version",
      method: "get",
    }),
  },
  section12: {
    // Admin endpoints — doctor search
    searchDoctorsAdmin: (query: string, limit: number, offset: number) => ({
      url: "/api/section12/admin/doctors/search",
      method: "get",
      params: { query, limit, offset },
    }),
    // Admin endpoints — specialisms
    createSpecialism: (body: CreateSection12SpecialismRequest) => ({
      url: "/api/section12/specialisms",
      method: "post",
      data: body,
    }),
    updateSpecialism: (
      specialismId: string,
      body: UpdateSection12SpecialismRequest,
    ) => ({
      url: `/api/section12/specialisms/${specialismId}`,
      method: "put",
      data: body,
    }),
    deleteSpecialism: (specialismId: string) => ({
      url: `/api/section12/specialisms/${specialismId}`,
      method: "delete",
    }),
    // Get all specialisms (available to all authenticated users)
    getAllSpecialisms: () => ({
      url: "/api/section12/specialisms",
      method: "get",
    }),
    // Admin endpoints — languages
    createLanguage: (body: CreateSection12LanguageRequest) => ({
      url: "/api/section12/languages",
      method: "post",
      data: body,
    }),
    updateLanguage: (
      languageId: string,
      body: UpdateSection12LanguageRequest,
    ) => ({
      url: `/api/section12/languages/${languageId}`,
      method: "put",
      data: body,
    }),
    // Get all languages (available to all authenticated users)
    getAllLanguages: () => ({
      url: "/api/section12/languages",
      method: "get",
    }),
    // User self-management endpoints
    setUserAvailability: (
      userId: string,
      body: SetSection12AvailabilityBlocksRequestUnbranded,
    ) => ({
      url: `/api/section12/user/${userId}/availability`,
      method: "put",
      data: body,
    }),
    // Without a range the server answers with today onwards; naming one is how
    // a caller reaches a date already past.
    getUserAvailabilityOverrides: (
      userId: string,
      range?: DateRangeUnbranded,
    ) => ({
      url: `/api/section12/user/${userId}/availability-overrides`,
      method: "get",
      params:
        range === undefined
          ? undefined
          : { fromDate: range.from, toDate: range.to },
    }),
    setUserAvailabilityOverrides: (
      userId: string,
      body: SetSection12AvailabilityOverrideDatesRequestUnbranded,
    ) => ({
      url: `/api/section12/user/${userId}/availability-overrides`,
      method: "put",
      data: body,
    }),
    deleteUserAvailabilityOverrides: (
      userId: string,
      range: DateRangeUnbranded,
    ) => ({
      url: `/api/section12/user/${userId}/availability-overrides`,
      method: "delete",
      params: { fromDate: range.from, toDate: range.to },
    }),
    setUserSpecialisms: (
      userId: string,
      body: SetSection12SpecialismsRequest,
    ) => ({
      url: `/api/section12/user/${userId}/specialisms`,
      method: "put",
      data: body,
    }),
    setUserLocalAuthorityMappings: (
      userId: string,
      body: SetSection12LocalAuthorityMappingsRequest,
    ) => ({
      url: `/api/section12/user/${userId}/local-authority-mappings`,
      method: "put",
      data: body,
    }),
    // Phone verification endpoints
    getPhoneStatus: (userId: string) => ({
      url: `/api/section12/user/${userId}/phone`,
      method: "get",
    }),
    sendPhoneVerificationCode: (
      userId: string,
      body: SendSection12PhoneVerificationCodeRequest,
    ) => ({
      url: `/api/section12/user/${userId}/phone/send-code`,
      method: "post",
      data: body,
    }),
    confirmPhone: (userId: string, body: ConfirmSection12PhoneRequest) => ({
      url: `/api/section12/user/${userId}/phone/confirm`,
      method: "put",
      data: body,
    }),
    removePhone: (userId: string) => ({
      url: `/api/section12/user/${userId}/phone`,
      method: "delete",
    }),
    setUserGender: (userId: string, body: SetSection12GenderRequest) => ({
      url: `/api/section12/user/${userId}/gender`,
      method: "put",
      data: body,
    }),
    setUserLanguages: (userId: string, body: SetSection12LanguagesRequest) => ({
      url: `/api/section12/user/${userId}/languages`,
      method: "put",
      data: body,
    }),
    setUserTrustAffiliation: (
      userId: string,
      body: SetSection12TrustAffiliationRequest,
    ) => ({
      url: `/api/section12/user/${userId}/trust-affiliation`,
      method: "put",
      data: body,
    }),
    setUserMaxTravelTime: (
      userId: string,
      body: SetSection12MaxTravelTimeRequest,
    ) => ({
      url: `/api/section12/user/${userId}/max-travel-time`,
      method: "put",
      data: body,
    }),
    setUserContactPreferences: (
      userId: string,
      body: SetSection12ContactPreferencesRequest,
    ) => ({
      url: `/api/section12/user/${userId}/contact-preferences`,
      method: "put",
      data: body,
    }),
    setUserSpecialismsWithLevels: (
      userId: string,
      body: SetSection12SpecialismsWithLevelsRequest,
    ) => ({
      url: `/api/section12/user/${userId}/specialisms-with-levels`,
      method: "put",
      data: body,
    }),
    // S12 Doctor Search
    searchDoctors: (localAuthorityId: string, excludeInterviewId?: string) => ({
      url: `/api/section12/doctors`,
      method: "get",
      params: { localAuthorityId, excludeInterviewId },
    }),
    // S12 Payment Requests
    getTeamInvoicing: (formContextId: string, amhpTeamId: string) => ({
      url: `/api/form-context/${formContextId}/section12-invoicing/${amhpTeamId}`,
      method: "get" as const,
    }),
    searchAmhpUsers: (query: string, limit: number, offset: number) => ({
      url: "/api/section12/payment-requests/amhp-users/search",
      method: "get" as const,
      params: { query, limit, offset },
    }),
    createPaymentRequest: (body: CreateSection12PaymentRequestInput) => ({
      url: "/api/section12/payment-requests",
      method: "post",
      data: body,
    }),
    getMyPaymentRequests: (params: {
      status?: Section12PaymentRequestStatusFilter;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }) => ({
      url: "/api/section12/payment-requests/mine",
      method: "get",
      params,
    }),
    exportMyPaymentRequestsCsv: (params: {
      status?: Section12PaymentRequestStatusFilter;
      fromDate?: string;
      toDate?: string;
    }) => ({
      url: "/api/section12/payment-requests/mine/export-csv",
      method: "get",
      params,
      responseType: "blob" as const,
    }),
    getPaymentRequestsByLocalAuthorities: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }) => ({
      url: "/api/section12/payment-requests/by-local-authorities",
      method: "get",
      params,
    }),
    exportPaymentRequestsByLocalAuthoritiesCsv: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
    }) => ({
      url: "/api/section12/payment-requests/by-local-authorities/export-csv",
      method: "get",
      params,
      responseType: "blob" as const,
    }),
    getPaymentRequestsForFormContext: (formContextId: string) => ({
      url: `/api/section12/payment-requests/form-context/${formContextId}`,
      method: "get",
    }),
    getPaymentRequestById: (paymentRequestId: string) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}`,
      method: "get",
    }),
    getPaymentRequestReceiptDownloadUrl: (
      paymentRequestId: string,
      receiptId: string,
    ) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}/receipts/${receiptId}`,
      method: "get",
    }),
    updatePaymentRequestStatus: (
      paymentRequestId: string,
      body: UpdateSection12PaymentRequestStatusInput,
    ) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}/status`,
      method: "put",
      data: body,
    }),
    updatePaymentRequest: (
      paymentRequestId: string,
      body: UpdateSection12PaymentRequestInput,
    ) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}`,
      method: "put",
      data: body,
    }),
    limitedUpdatePaymentRequest: (
      paymentRequestId: string,
      body: LimitedUpdateSection12PaymentRequestInput,
    ) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}/limited-edit`,
      method: "patch",
      data: body,
    }),
    deletePaymentRequest: (paymentRequestId: string) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}`,
      method: "delete",
    }),
    getAllPaymentRequests: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }) => ({
      url: "/api/section12/payment-requests",
      method: "get",
      params,
    }),
    exportPaymentRequestsCsv: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      doctorUserId?: string;
      amhpTeamId?: string;
      localAuthorityId?: string;
    }) => ({
      url: "/api/section12/payment-requests/export-csv",
      method: "get",
      params,
      responseType: "blob" as const,
    }),
    exportFinancePaymentRequestsCsv: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      doctorUserId?: string;
      amhpTeamId?: string;
      localAuthorityId?: string;
    }) => ({
      url: "/api/section12/payment-requests/export-finance-csv",
      method: "get",
      params,
      responseType: "blob" as const,
    }),
    markPaymentRequestPaid: (paymentRequestId: string) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}/paid`,
      method: "put",
    }),
    adminUpdatePaymentStatus: (
      paymentRequestId: string,
      body: UpdateSection12PaymentRequestAdminStatusInput,
    ) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}/admin-status`,
      method: "put",
      data: body,
    }),
    adjustPaymentRequest: (
      paymentRequestId: string,
      body: AdjustSection12PaymentRequestInput,
    ) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}/adjust`,
      method: "put",
      data: body,
    }),
    getPaymentRequestChangeHistory: (paymentRequestId: string) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}/change-history`,
      method: "get",
    }),
    createXeroBill: (paymentRequestId: string, body?: { force?: boolean }) => ({
      url: `/api/section12/payment-requests/${paymentRequestId}/create-xero-bill`,
      method: "post",
      data: body ?? {},
    }),
    // Home Base Location
    setHomeBaseLocation: (
      userId: string,
      body: {
        address: string | null;
        postalCode: string | null;
        useProfileAddress?: boolean;
      },
    ) => ({
      url: `/api/section12/users/${userId}/home-base-location`,
      method: "put",
      data: body,
    }),
    // Route Times
    getRouteTimes: (body: { formContextId: string; userIds: string[] }) => ({
      url: "/api/section12/route-times",
      method: "post",
      data: body,
    }),
    // Calendar Sync
    getCalendarSyncConnections: (userId: string) => ({
      url: `/api/section12/user/${userId}/calendar-sync/connections`,
      method: "get",
    }),
    getCalendarSyncAuthUrl: (
      userId: string,
      body: GetCalendarSyncAuthUrlRequest,
    ) => ({
      url: `/api/section12/user/${userId}/calendar-sync/auth-url`,
      method: "post",
      data: body,
    }),
    exchangeCalendarSyncCode: (
      userId: string,
      body: ExchangeCalendarSyncCodeRequest,
    ) => ({
      url: `/api/section12/user/${userId}/calendar-sync/callback`,
      method: "post",
      data: body,
    }),
    deleteCalendarSyncConnection: (userId: string, connectionId: string) => ({
      url: `/api/section12/user/${userId}/calendar-sync/connections/${connectionId}`,
      method: "delete",
    }),
    setCalendarSyncEnabled: (userId: string, enabled: boolean) => ({
      url: `/api/section12/user/${userId}/calendar-sync/enabled`,
      method: "put",
      data: { enabled },
    }),
    triggerCalendarSync: (userId: string, connectionId: string) => ({
      url: `/api/section12/user/${userId}/calendar-sync/connections/${connectionId}/sync`,
      method: "post",
    }),
    triggerCalendarSyncAll: (userId: string) => ({
      url: `/api/section12/user/${userId}/calendar-sync/sync`,
      method: "post",
    }),
    connectCalendarFeed: (
      userId: string,
      body: ConnectCalendarFeedRequest,
    ) => ({
      url: `/api/section12/user/${userId}/calendar-sync/connect-feed`,
      method: "post",
      data: body,
    }),
  },
  interviews: {
    create: (body: CreateInterviewInput) => ({
      url: "/api/interviews",
      method: "post",
      data: body,
    }),
    getForFormContext: (formContextId: string) => ({
      url: `/api/interviews/form-context/${formContextId}`,
      method: "get",
    }),
    getMyInterviews: (formContextId: string) => ({
      url: `/api/interviews/form-context/${formContextId}/my`,
      method: "get",
    }),
    get: (interviewId: string) => ({
      url: `/api/interviews/${interviewId}`,
      method: "get",
    }),
    update: (interviewId: string, body: UpdateInterviewInput) => ({
      url: `/api/interviews/${interviewId}`,
      method: "put",
      data: body,
    }),
    cancel: (interviewId: string) => ({
      url: `/api/interviews/${interviewId}`,
      method: "delete",
    }),
    addParticipant: (
      interviewId: string,
      body: AddInterviewParticipantInput,
    ) => ({
      url: `/api/interviews/${interviewId}/participants`,
      method: "post",
      data: body,
    }),
    confirmParticipant: (
      interviewId: string,
      participantId: string,
      body?: ConfirmParticipantInput,
    ) => ({
      url: `/api/interviews/${interviewId}/participants/${participantId}/confirm`,
      method: "post",
      data: body,
    }),
    revokeParticipant: (interviewId: string, participantId: string) => ({
      url: `/api/interviews/${interviewId}/participants/${participantId}/revoke`,
      method: "post",
    }),
    unconfirmParticipant: (interviewId: string, participantId: string) => ({
      url: `/api/interviews/${interviewId}/participants/${participantId}/unconfirm`,
      method: "post",
    }),
    restoreParticipants: (interviewId: string) => ({
      url: `/api/interviews/${interviewId}/restore-participants`,
      method: "post",
    }),
    getInvite: (shareCode: string) => ({
      url: `/api/interviews/invite/${shareCode}`,
      method: "get",
    }),
    acceptInvite: (shareCode: string, body?: AcceptInterviewInviteInput) => ({
      url: `/api/interviews/invite/${shareCode}/accept`,
      method: "post",
      data: body,
    }),
    rejectInvite: (shareCode: string, body: RejectInterviewInviteInput) => ({
      url: `/api/interviews/invite/${shareCode}/reject`,
      method: "post",
      data: body,
    }),
    getInviteRouteTime: (shareCode: string) => ({
      url: `/api/interviews/invite/${shareCode}/route-time`,
      method: "get",
    }),
    getRouteTimes: (interviewId: string, body: { userIds: string[] }) => ({
      url: `/api/interviews/${interviewId}/route-times`,
      method: "post",
      data: body,
    }),
  },
  tortusAvt: {
    getLaunchToken: (body: GetTortusAvtLaunchTokenRequest) => ({
      url: `/api/tortus-avt/launch-token`,
      method: "post",
      data: body,
    }),
  },
  userSessionEvents: {
    record: (body: RecordUserSessionEventRequest) => ({
      url: `/api/user-session-events`,
      method: "post",
      data: body,
    }),
  },
};

export const api = {
  pathway: {
    getPatientPathways: () =>
      apiAxios.request<PathwayPatientPathwaysResponse>(
        apiConfig.pathway.getPatientPathways(),
      ),
    getPatientDetentionPeriods: (rootPatientId: string) =>
      apiAxios.request<PathwaySinglePatientDetentionPeriodsResponse>(
        apiConfig.pathway.getPatientDetentionPeriods(rootPatientId),
      ),
    getDetentionPeriods: () =>
      apiAxios.request<PathwayDetentionPeriodsResponse>(
        apiConfig.pathway.getDetentionPeriods(),
      ),
    getFacets: () =>
      apiAxios.request<PathwayFacetsResponse>(apiConfig.pathway.getFacets()),
    getPatientRows: (filters: PathwayPatientRowsQuery) =>
      apiAxios.request<PathwayPatientRowsResponse>(
        apiConfig.pathway.getPatientRows(filters),
      ),
    getCohortStats: (query: PathwayCohortQuery) =>
      apiAxios.request<PathwayCohortStatsResponse>(
        apiConfig.pathway.getCohortStats(query),
      ),
    getPatientTimelines: (rootPatientIds: string[]) =>
      apiAxios.request<PathwayTimelinesResponse>(
        apiConfig.pathway.getPatientTimelines(rootPatientIds),
      ),
  },
  connectivity: {
    getConnectivity: (params?: {
      from?: string;
      to?: string;
      window?: string;
    }) =>
      apiAxios.request<ConnectivityResponse>(
        apiConfig.connectivity.getConnectivity(params),
      ),
  },
  soadUserTeamOrg: {
    create: (request: CreateSoadRequest) =>
      apiAxios.request<void>(apiConfig.soadUserTeamOrg.create(request)),
    delete: (request: DeleteSoadContextRequest) =>
      apiAxios.request<void>(apiConfig.soadUserTeamOrg.delete(request)),
  },
  soad: {
    startAmendment: (
      formContextId: string,
      reasoning: { reason: string; reasonDescription?: string },
    ) =>
      apiAxios.request<void>(
        apiConfig.soad.startAmendment(formContextId, reasoning),
      ),
    cancelAmendment: (formContextId: string) =>
      apiAxios.request<void>(apiConfig.soad.cancelAmendment(formContextId)),
    signAndShareSoadAssessment: (
      formContextId: string,
      request: SignSoadAssessmentRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.soad.signAndShareSoadAssessment(formContextId, request),
      ),
    getPdf: (formContextId: string, formId: string, version?: number) =>
      apiAxios.request<string>(
        apiConfig.soad.getPdf(formContextId, formId, version),
      ),
    addStatutoryCertificate: (formContextId: string, certificateType: string) =>
      apiAxios.request<void>(
        apiConfig.soad.addStatutoryCertificate(formContextId, certificateType),
      ),
    deleteStatutoryCertificate: (
      formContextId: string,
      certificateType: string,
    ) =>
      apiAxios.request<void>(
        apiConfig.soad.deleteStatutoryCertificate(
          formContextId,
          certificateType,
        ),
      ),
    delete: (formContextId: string) =>
      apiAxios.request<void>(apiConfig.soad.delete(formContextId)),
  },

  patientList: {
    add: (patientId: string) =>
      apiAxios.request<void>(apiConfig.patientList.add(patientId)),
    remove: (patientId: string) =>
      apiAxios.request<void>(apiConfig.patientList.remove(patientId)),
  },
  capacityState: {
    register: (careHomeIds?: string[], residentSearchFilter?: string) =>
      apiAxios.request<RegisterResponse>(
        apiConfig.capacityState.register(careHomeIds, residentSearchFilter),
      ),
    history: (patientId: string) =>
      apiAxios.request<CapacityStateWithContext[]>(
        apiConfig.capacityState.history(patientId),
      ),
    addResident: (data: AddResidentRequest) =>
      apiAxios.request<void>(apiConfig.capacityState.addResident(data)),
    removeResident: (patientId: string, data: RemoveResidentRequest) =>
      apiAxios.request<void>(
        apiConfig.capacityState.removeResident(patientId, data),
      ),
    transferResident: (patientId: string, data: TransferResidentRequest) =>
      apiAxios.request<void>(
        apiConfig.capacityState.transferResident(patientId, data),
      ),
    recordStatus: (patientId: string, data: RecordCapacityStatusRequest) =>
      apiAxios.request<void>(
        apiConfig.capacityState.recordStatus(patientId, data),
      ),
  },
  s132: {
    recordAttempt: (patientId: string, data: RecordS132AttemptRequest) =>
      apiAxios.request<S132Record>(
        apiConfig.s132.recordAttempt(patientId, data),
      ),
    correctAttempt: (
      patientId: string,
      attemptId: string,
      data: CorrectS132AttemptRequest,
    ) =>
      apiAxios.request<S132Record>(
        apiConfig.s132.correctAttempt(patientId, attemptId, data),
      ),
    withdrawAttempt: (
      patientId: string,
      attemptId: string,
      data: WithdrawS132AttemptRequest,
    ) =>
      apiAxios.request<S132Record>(
        apiConfig.s132.withdrawAttempt(patientId, attemptId, data),
      ),
  },

  patientState: {
    csvReport: (
      request: PatientStateCsvRequest,
      statuses: MhaStatus[],
      expiryLessThan?: string,
      trustIds?: string[],
      hospitalIds?: string[],
    ) =>
      apiAxios.request<string>(
        apiConfig.patientState.csvReport(
          request,
          statuses,
          expiryLessThan,
          trustIds,
          hospitalIds,
        ),
      ),
  },
  patientDemographics: {
    clearAlertsForPatient: (patientId: string) =>
      apiAxios.request<void>(
        apiConfig.patientDemographics.clearAlertsForPatient(patientId),
      ),
  },
  vision: {
    validateShareCode: (shareCode: string) =>
      apiAxios.request<
        | Omit<ValidateShareCodeV1Response, "incidentId">
        | ValidateShareCodeV1Response
      >(apiConfig.vision.validateShareCode(shareCode)),
    accessRequest: (accessRequest: VisionAccessRequest) =>
      apiAxios.request<VisionAccessRequestResponse>(
        apiConfig.vision.accessRequest(accessRequest),
      ),
    previewIncident: (incidentId: string) =>
      apiAxios.request<PreviewVisionIncidentReponse>(
        apiConfig.vision.previewIncident(incidentId),
      ),
    acceptIncident: (data: VisionAcceptIncidentRequest) =>
      apiAxios.request<AcceptVisionIncidentResponse>(
        apiConfig.vision.acceptIncident(data),
      ),
    rejectIncident: (incidentId: string, reason: string) =>
      apiAxios.request<void>(
        apiConfig.vision.rejectIncident(incidentId, reason),
      ),
    synchroniseIncidentForFormContext: (formContextId: string) =>
      apiAxios.request<ShareableIncidentDataV1Response>(
        apiConfig.vision.synchroniseIncidentForFormContext(formContextId),
      ),
    getIncidentIdForFormContext: (formContextId: string) =>
      apiAxios.request<VisionIncidentIdResponse>(
        apiConfig.vision.getIncidentIdForFormContext(formContextId),
      ),

    requestPolicePresenceV2: (
      formContextId: string,
      recordPolicePresenceAction: RecordPolicePresenceV2Action,
    ) =>
      apiAxios.request<void>(
        apiConfig.vision.requestPolicePresenceV2(
          formContextId,
          recordPolicePresenceAction,
        ),
      ),
  },
  outcomeForm: {
    getForFormContext: (formContextId: string) =>
      apiAxios.request<OutcomeFormSummary[]>(
        apiConfig.outcomeForm.getForFormContext(formContextId),
      ),
    create: (request: CreateOutcomeFormRequest) =>
      apiAxios.request<CreateOutcomeFormResponse>(
        apiConfig.outcomeForm.create(request),
      ),
    claim: (formId: string, request: ClaimOutcomeFormRequest) =>
      apiAxios.request<ClaimOutcomeFormResponse>(
        apiConfig.outcomeForm.claim(formId, request),
      ),
    complete: (formId: string) =>
      apiAxios.request<CompleteOutcomeFormResponse>(
        apiConfig.outcomeForm.complete(formId),
      ),
  },
  singleUseActionToken: {
    create: (id: UUID, body: CreateSingleUseActionTokenRequest) =>
      apiAxios.request<void>(apiConfig.singleUseActionToken.create(id, body)),
    revoke: (id: UUID) =>
      apiAxios.request<void>(apiConfig.singleUseActionToken.revoke(id)),
    use: (id: UUID, csv: string, dryRun: boolean) =>
      apiAxios.request<UseSingleUseActionTokenResponse>(
        apiConfig.singleUseActionToken.use(id, csv, dryRun),
      ),
    downloadTemplate: (id: UUID) =>
      apiAxios.request<string>(
        apiConfig.singleUseActionToken.downloadTemplate(id),
      ),
  },
  system: {
    generatePresignedUploadUrl: (body: GeneratePresignedUrlRequestBody) =>
      apiAxios.request<GeneratePresignedUrlResponseBody>(
        apiConfig.system.generatePresignedUploadUrl(body),
      ),
    checkS12Approval: (gmcNumber: string) =>
      apiAxios.request<{ surname: string; section12ExpiryDate: string }>(
        apiConfig.system.checkS12Approval(gmcNumber),
      ),
  },

  termsAndConditions: {
    getAll: () =>
      apiAxios.request<TermsAndConditionsResponse>(
        apiConfig.termsAndConditions.getAll(),
      ),
    get: (id: UUID) =>
      apiAxios.request<TermsAndConditions>(
        apiConfig.termsAndConditions.get(id),
      ),
    create: (id: UUID, req: CreateTermsAndConditionsRequest) =>
      apiAxios.request<void>(apiConfig.termsAndConditions.create(id, req)),
    accept: (req: AcceptTermsAndConditionsRequest) =>
      apiAxios.request<void>(apiConfig.termsAndConditions.accept(req)),
  },

  admissions: {
    delete: (workItemId: string) =>
      apiAxios.request<void>(apiConfig.admissions.delete(workItemId)),
    recordReasonForNotProceedingMedRec: (
      workItemId: string,
      reason: string,
      reasonDescription?: string,
    ) =>
      apiAxios.request(
        apiConfig.admissions.recordReasonForNotProceedingMedRec(
          workItemId,
          reason,
          reasonDescription,
        ),
      ),
    recordReasonForNotProceedingAppForm: (
      workItemId: string,
      reason: string,
      reasonDescription?: string,
    ) =>
      apiAxios.request(
        apiConfig.admissions.recordReasonForNotProceedingAppForm(
          workItemId,
          reason,
          reasonDescription,
        ),
      ),

    reOpenAdmission: (
      workItemId: string,
      reason: string,
      reasonDescription?: string,
    ) =>
      apiAxios.request(
        apiConfig.admissions.reOpenAdmission(
          workItemId,
          reason,
          reasonDescription,
        ),
      ),
  },
  admin: {
    getGuestUserLoginCode: (userId: string) =>
      apiAxios.request<{
        code: string;
        url: string;
        created: string;
      }>(apiConfig.admin.getGuestUserLoginCode(userId)),
    getDataPermissionInfo: (
      resourceType: "patient" | "form-context",
      resourceId: string,
    ) =>
      apiAxios.request<AdminGetDataPermissionInfoResponse>(
        apiConfig.admin.getDataPermissionInfo(resourceType, resourceId),
      ),
    deleteDataPermission: (
      resourceType: "patient" | "form-context",
      resourceId: string,
      dataControllerId: string,
      ticketNumber: string,
    ) =>
      apiAxios.request<void>(
        apiConfig.admin.deleteDataPermission(
          resourceType,
          resourceId,
          dataControllerId,
          ticketNumber,
        ),
      ),
    deleteWorkItem: (
      workItemId: string,
      reason: string,
      ticketNumber: string,
    ) =>
      apiAxios.request<{ id: string; teamId: string; formContextId: string }>(
        apiConfig.admin.deleteWorkItem(workItemId, reason, ticketNumber),
      ),
    getPatientDetails: (data: AdminPatientDetailsRequest) =>
      apiAxios.request<AdminPatientDetailsResponse>(
        apiConfig.admin.getPatientDetails(data),
      ),
  },
  upload: {
    uploadForm: (
      formContextId: string,
      formId: string,
      data: UploadFormRequest,
    ) =>
      apiAxios.request(apiConfig.forms.uploadForm(formContextId, formId, data)),
  },
  reports: {
    // Directly returns a CSV string
    // TODO: send a signed s3 url to download instead?
    generateSignedFormsCsv: (params: SignedFormsCsvParams) =>
      apiAxios.request<string>(
        apiConfig.reports.generateSignedFormsCsv(params),
      ),
    exportSupersetDashboard: (
      organisationId: string,
      supersetDashboardId: string,
      format: "pdf" | "png",
      activeTabs?: string[],
      anchor?: string,
      dataMask?: Record<string, unknown>,
    ) =>
      apiAxios.request<SupersetDashboardExportResponse>(
        apiConfig.reports.exportSupersetDashboard(
          organisationId,
          supersetDashboardId,
          format,
          activeTabs,
          anchor,
          dataMask,
        ),
      ),
  },
  configuration: {
    updatePdsConfiguration: (configuration: {
      apiKey: string;
      endpoint: string;
      jwtKid: string;
      privateKey: string;
    }) =>
      apiAxios.request<void>(
        apiConfig.configuration.updatePdsConfiguration(configuration),
      ),
    updateRafConfiguration: (configuration: {
      apiKey: string;
      endpoint: string;
      jwtKid: string;
      privateKey: string;
    }) =>
      apiAxios.request<void>(
        apiConfig.configuration.updateRafConfiguration(configuration),
      ),
    addAdminManagedLocation: (
      location: Omit<ManagedLocation, "id" | "status">,
    ) => apiAxios.request<void>(apiConfig.configuration.addLocation(location)),
    getLocationsByIds: (ids: string[]) =>
      apiAxios.request<ManagedLocation[]>(
        apiConfig.configuration.getLocationsByIds(ids),
      ),
    updateAdminManagedLocation: (
      id: string,
      location: Omit<ManagedLocation, "id" | "status">,
    ) =>
      apiAxios.request<void>(
        apiConfig.configuration.updateLocation(id, location),
      ),
    deleteAdminManagedLocation: (id: ManagedLocation["id"]) =>
      // A refusal carries its reason; a 204 carries no body.
      apiAxios.request<{ reason: string } | "">(
        apiConfig.configuration.deleteLocation(id),
      ),
    updateLocalAuthorityPaymentRates: (
      id: string,
      rates: LocalAuthorityPaymentConfiguration,
    ) =>
      apiAxios.request<void>(
        apiConfig.configuration.updateLocalAuthorityPaymentRates(id, rates),
      ),
    updateHospitalMhlAutoRouteConfig: (
      id: string,
      config: {
        teams: { teamId: string }[];
        guestEmails: string[];
      } | null,
    ) =>
      apiAxios.request<{ config: HospitalMhlAutoRouteConfig | null }>(
        apiConfig.configuration.updateHospitalMhlAutoRouteConfig(id, config),
      ),
    addApprovedEmailDomain: (domain: string) =>
      apiAxios.request(apiConfig.configuration.addApprovedEmailDomain(domain)),
    removeApprovedEmailDomain: (domain: string) =>
      apiAxios.request(
        apiConfig.configuration.removeApprovedEmailDomain(domain),
      ),
    addEmailRedirect: (
      sourceEmail: string,
      targetTeamId: string,
      message?: string,
    ) =>
      apiAxios.request(
        apiConfig.configuration.addEmailRedirect(
          sourceEmail,
          targetTeamId,
          message,
        ),
      ),
    removeEmailRedirect: (id: string) =>
      apiAxios.request(apiConfig.configuration.removeEmailRedirect(id)),
    getActiveEmailRedirects: () =>
      apiAxios.request<
        {
          sourceEmail: string;
          targetTeamId: string;
          targetTeamName: string | null;
          message: string | null;
        }[]
      >(apiConfig.configuration.getActiveEmailRedirects()),
    updateOrganisationConfiguration: (
      organisationId: string,
      configuration: Partial<OrganisationConfiguration>,
    ) =>
      apiAxios.request(
        apiConfig.configuration.updateOrganisationConfiguration(
          organisationId,
          configuration,
        ),
      ),
    updateS12ApprovalConfiguration: (configuration: {
      endpoint: string;
      username: string;
      password: string;
    }) =>
      apiAxios.request<void>(
        apiConfig.configuration.updateS12ApprovalConfiguration(configuration),
      ),
    updateOdsConfiguration: (configuration: {
      endpoint: string;
      apiKey: string;
    }) =>
      apiAxios.request<void>(
        apiConfig.configuration.updateOdsConfiguration(configuration),
      ),
    updateXeroConfiguration: (configuration: Record<string, unknown>) =>
      apiAxios.request<void>(
        apiConfig.configuration.updateXeroConfiguration(configuration),
      ),
    setUserXeroContactId: (userId: string, xeroContactId: string | null) =>
      apiAxios.request<undefined>(
        apiConfig.configuration.setUserXeroContactId(userId, xeroContactId),
      ),
    getUserXeroContactId: (userId: string) =>
      apiAxios.request<{ xeroContactId: string | null }>(
        apiConfig.configuration.getUserXeroContactId(userId),
      ),
    searchXeroContacts: (query: string) =>
      apiAxios.request<
        Array<{ contactId: string; name: string; emailAddress: string | null }>
      >(apiConfig.configuration.searchXeroContacts(query)),
  },
  ods: {
    getOrganisation: (odsCode: string) =>
      apiAxios.request<OdsOrganisationDetails>(
        apiConfig.ods.getOrganisation(odsCode),
      ),
    getLocalAuthorityByPostcode: (postcode: string) =>
      apiAxios.request<LocalAuthorityDetails>(
        apiConfig.ods.getLocalAuthorityByPostcode(postcode),
      ),
    getLocalAuthorityByOdsCode: (odsCode: string) =>
      apiAxios.request<LocalAuthorityDetails>(
        apiConfig.ods.getLocalAuthorityByOdsCode(odsCode),
      ),
    getIcbByGpOdsCode: (gpOdsCode: string) =>
      apiAxios.request<IcbDetails>(apiConfig.ods.getIcbByGpOdsCode(gpOdsCode)),
  },
  work: {
    create: (workItemId: string, createRequest: CreateWorkItemRequest) =>
      apiAxios.request(apiConfig.work.create(workItemId, createRequest)),
    accept: (workItemId: string) =>
      apiAxios.request(apiConfig.work.accept(workItemId)),
    claim: (workItemId: string) =>
      apiAxios.request(apiConfig.work.claim(workItemId)),
    unClaim: (workItemId: string) =>
      apiAxios.request(apiConfig.work.unClaim(workItemId)),
    reject: (
      workItemId: string,
      rejectionReason: string,
      rejectReasonDescription?: string,
    ) =>
      apiAxios.request(
        apiConfig.work.reject(
          workItemId,
          rejectionReason,
          rejectReasonDescription,
        ),
      ),

    archive: (workItemId: string) =>
      apiAxios.request(apiConfig.work.archive(workItemId)),
    unArchive: (workItemId: string) =>
      apiAxios.request(apiConfig.work.unArchive(workItemId)),
    finalise: (workItemId: string) =>
      apiAxios.request(apiConfig.work.finalise(workItemId)),
    unfinalise: (workItemId: string, req: UnfinaliseWorkItemRequest) =>
      apiAxios.request(apiConfig.work.unfinalise(workItemId, req)),
    cancel: (workItemId: string) =>
      apiAxios.request(apiConfig.work.cancel(workItemId)),
    bulkArchive: (workItemIds: string[]) =>
      apiAxios.request(apiConfig.work.bulkArchive(workItemIds)),
  },
  bulkDownload: {
    create: (
      filesToAdd: {
        fileName: string;
        formContextId: string;
      }[],
    ) =>
      apiAxios.request<{ downloadId: string }>(
        apiConfig.bulkDownload.create(filesToAdd),
      ),
    poll: (bulkDownloadId: string) =>
      apiAxios.request(apiConfig.bulkDownload.poll(bulkDownloadId)),
  },
  formTemplates: {
    searchAssignmentOptions: (search: FormPartAssignmentOptionsSearch) =>
      apiAxios.request<{ results: UserOrTeamSearchResult[] }>(
        apiConfig.formTemplates.searchAssignmentOptions(search),
      ),
  },
  users: {
    search: (
      query: string,
      limit: number,
      offset: number,
      managingOrganisationId?: UUID,
    ) =>
      apiAxios.request<{
        results: Omit<ExtendedThalamosUser, "defaultSignature">[];
        count: number;
      }>(apiConfig.users.search(query, limit, offset, managingOrganisationId)),
    searchPersonalDataDownloadRecipients: (
      query: string,
      limit: number,
      offset: number,
    ) =>
      apiAxios.request<{
        results: { id: string; name: string; email: string }[];
        count: number;
      }>(
        apiConfig.users.searchPersonalDataDownloadRecipients(
          query,
          limit,
          offset,
        ),
      ),
    me: (contextId: string | null) =>
      apiAxios.request<ExtendedThalamosUser | null>(
        apiConfig.users.me(contextId),
      ),
    loggedInCheck: () =>
      apiAxios.request<null>(apiConfig.users.loggedInCheck()),
    get: (userId: string) =>
      apiAxios.request<ExtendedThalamosUser | null>(
        apiConfig.users.get(userId),
      ),
    update: (userId: string, query: UserUpdate) =>
      apiAxios.request<ExtendedThalamosUser | null>(
        apiConfig.users.update(userId, query),
      ),
    checkS12ApprovalForUser: (userId: string, gmcNumber: string) =>
      apiAxios.request<{
        status: "found" | "not-found" | "error";
        surname?: string;
        section12ExpiryDate?: string;
        updated: boolean;
      }>(apiConfig.users.checkS12ApprovalForUser(userId, gmcNumber)),
    updateEmail: (userId: string, newEmail: string) =>
      apiAxios.request<ExtendedThalamosUser | null>(
        apiConfig.users.updateEmail(userId, newEmail),
      ),
    convertGuestUser: (userId: string, query: GuestUserConversionRequest) =>
      apiAxios.request<void>(apiConfig.users.convertGuestUser(userId, query)),
    convertFullUserToGuest: (userId: string) =>
      apiAxios.request(apiConfig.users.convertFullUserToGuest(userId)),
    create: (userId: string, body: UserCreate) =>
      apiAxios.request<void>(apiConfig.users.create(userId, body)),
    bulkConfigure: (csvData: string, dryRun: boolean) =>
      apiAxios.request<void>(apiConfig.users.bulkConfigure(csvData, dryRun)),
    bulkCreate: (
      csvData: string,
      dryRun: boolean,
      managingOrganisationId?: UUID,
    ) =>
      apiAxios.request<void>(
        apiConfig.users.bulkCreate(csvData, dryRun, managingOrganisationId),
      ),
    delete: (userId: string) =>
      apiAxios.request<void>(apiConfig.users.delete(userId)),
    createIndependentContext: (userId: string) =>
      apiAxios.request(apiConfig.users.createIndependentContext(userId)),
    deleteIndependentContext: (userId: string) =>
      apiAxios.request(apiConfig.users.deleteIndependentContext(userId)),
    revokeDeviceSession: (userId: string, sessionId: string) =>
      apiAxios.request<void>(
        apiConfig.users.revokeDeviceSession(userId, sessionId),
      ),
    revokeAllDeviceSessions: (userId: string) =>
      apiAxios.request<void>(apiConfig.users.revokeAllDeviceSessions(userId)),
    setOrganisationConfiguration: (
      userId: string,
      organisationId: string,
      configuration: UserOrganisationConfiguration,
    ) =>
      apiAxios.request(
        apiConfig.users.setOrganisationConfiguration(
          userId,
          organisationId,
          configuration,
        ),
      ),
    setSessionContext: (membership: Membership) =>
      apiAxios.request<void>(apiConfig.users.setSessionContext(membership)),
    requestGuestUserLoginCode: (userId: string, forceSend?: boolean) =>
      apiAxios.request<{ email: string; sent: boolean }>(
        apiConfig.users.requestGuestUserLoginCode(userId, forceSend),
      ),
    resetGuestUserLogins: (userId: string) =>
      apiAxios.request<{ email: string; sent: boolean }>(
        apiConfig.users.resetGuestUserLogins(userId),
      ),
    getGuestSessionAccreditation: () =>
      apiAxios.request<GuestSessionAccreditation | null>(
        apiConfig.users.getGuestSessionAccreditation(),
      ),
    submitGuestSessionAccreditation: (data: {
      fullName: string;
      gmcNumber: string;
      isSection12Approved: boolean;
      overrideApproval?: boolean;
      section12ExpiryDate?: string;
    }) =>
      apiAxios.request<GuestSessionAccreditation>(
        apiConfig.users.submitGuestSessionAccreditation(data),
      ),
    lookupGmcNumber: (gmcNumber: string) =>
      apiAxios.request<{
        found: boolean;
        section12ExpiryDate?: string;
      }>(apiConfig.users.lookupGmcNumber(gmcNumber)),
    ensureGuestUser: (email: string, name: string) =>
      apiAxios.request<{ userId: string; teamId: string }>(
        apiConfig.users.ensureGuestUser(email, name),
      ),
    tryGuestUserLoginCode: (userId: string, code: string) =>
      apiAxios.request<void>(
        apiConfig.users.tryGuestUserLoginCode(userId, code),
      ),
    triggerPasswordResetEmail: (userId: string) =>
      apiAxios.request<void>(apiConfig.users.triggerPasswordResetEmail(userId)),
    allowSmsMfa: (userId: string) =>
      apiAxios.request<void>(apiConfig.users.allowSmsMfa(userId)),
    disallowSmsMfa: (userId: string) =>
      apiAxios.request<void>(apiConfig.users.disallowSmsMfa(userId)),
    enforceMfa: (userId: string) =>
      apiAxios.request<void>(apiConfig.users.enforceMfa(userId)),
    resetMfa: (userId: string) =>
      apiAxios.request<void>(apiConfig.users.resetMfa(userId)),
    disableMfa: (userId: string) =>
      apiAxios.request<void>(apiConfig.users.disableMfa(userId)),
    addFavouriteRecipient: (favourite: AddFavouriteRecipientRequest) =>
      apiAxios.request<void>(apiConfig.users.addFavouriteRecipient(favourite)),
    removeFavouriteRecipient: (
      dataControllerId: string,
      teamId: string,
      userId?: string,
    ) =>
      apiAxios.request<void>(
        apiConfig.users.removeFavouriteRecipient(
          dataControllerId,
          teamId,
          userId,
        ),
      ),
  },
  notifications: {
    markAsViewed: (notificationIds: string[]) =>
      apiAxios.request(apiConfig.notifications.markAsViewed(notificationIds)),
  },
  patients: {
    get: (patientId: string) =>
      apiAxios.request<ExtendedPatient | null>(
        apiConfig.patients.get(patientId),
      ),
    search: (query: PatientIndexQuery) =>
      apiAxios.request<PatientIndexSearchResult>(
        apiConfig.patients.search(query),
      ),

    searchV2: (searchId: UUID, query: PatientSearchRequestV2) =>
      apiAxios.request<PatientIndexSearchResultV2>(
        apiConfig.patients.searchV2(searchId, query),
      ),

    markAlertAsSeen(patientId: string, alertId: string) {
      return apiAxios.request<null>(
        apiConfig.patients.markAlertAsSeen(patientId, alertId),
      );
    },

    getNhsFlags: (patientId: string) =>
      apiAxios.request<ReasonableAdjustmentsFlagResponse>(
        apiConfig.patients.getNhsFlags(patientId),
      ),

    addSearchV2Reasoning: (
      resultId: UUID,
      body: SaveSearchReasoningV2Request,
    ) =>
      apiAxios.request<SaveSearchReasoningV2Response>(
        apiConfig.patients.addSearchV2Reasoning(resultId, body),
      ),

    saveSearchReasoningToSession: (query: SearchReasoning) =>
      apiAxios.request<PatientIndexSearchResult>(
        apiConfig.patients.saveSearchReasoningToSession(query),
      ),
    create: (patientId: string, createPatient: CreatePatientRequest) =>
      apiAxios.request<Patient>(
        apiConfig.patients.create(patientId, createPatient),
      ),
    createFromExternalId: (request: CreatePatientFromExternalIdRequest) =>
      apiAxios.request<CreatePatientFromExternalIdResponse>(
        apiConfig.patients.createFromExternalId(request),
      ),
    getExternalPatientLinks: (
      patientId: string,
      patientIdsToTreatAsRootPatients?: string[],
    ) =>
      apiAxios.request<GetExternalPatientLinksResponse>(
        apiConfig.patients.getExternalPatientLinks(patientId, {
          patientIdsToTreatAsRootPatients:
            patientIdsToTreatAsRootPatients ?? [],
        }),
      ),
    createExternalPatientDemographicsPullEvent: (
      patientId: string,
      request: CreateExternalPatientDemograhicsPullEventRequest,
    ) =>
      apiAxios.request<CreateExternalPatientDemograhicsPullEventResponse>(
        apiConfig.patients.createExternalPatientDemographicsPullEvent(
          patientId,
          request,
        ),
      ),
    updateExternalPatientDemographicsPullEvent: (
      patientId: string,
      pullEventId: string,
      request: UpdateExternalPatientDemograhicsPullEventRequest,
      choiceDifferences?: ChoiceDifferencesEntry[],
    ) =>
      apiAxios.request<UpdateExternalPatientDemograhicsPullEventResponse>(
        apiConfig.patients.updateExternalPatientDemographicsPullEvent(
          patientId,
          pullEventId,
          request,
          choiceDifferences,
        ),
      ),
    getExternalPatientDemographicsPullEvent: (
      patientId: string,
      pullEventId: string,
    ) =>
      apiAxios.request<GetExternalPatientDemograhicsPullEventResponse>(
        apiConfig.patients.getExternalPatientDemographicsPullEvent(
          patientId,
          pullEventId,
        ),
      ),
    updatePatientMhaStateWithManualEvent: (
      patientId: string,
      request: UpdatePatientStateWithManualEventRequest,
    ) =>
      apiAxios.request<null>(
        apiConfig.patients.updatePatientMhaStateWithManualEvent(
          patientId,
          request,
        ),
      ),
    getPatientMhaState: (patientId: string) =>
      apiAxios.request<GetPatientMhaStateResponse>(
        apiConfig.patients.getPatientMhaState(patientId),
      ),
    getSection136PlaceOfSafety: (patientId: string) =>
      apiAxios.request<GetSection136PlaceOfSafetyResponse>(
        apiConfig.patients.getSection136PlaceOfSafety(patientId),
      ),
    update: (patientId: string, updatePatient: UpdatePatientRequest) =>
      apiAxios.request<Patient | null>(
        apiConfig.patients.update(patientId, updatePatient),
      ),
    merge: (query: PatientMergeInfo) =>
      apiAxios.request<PatientIndexSearchResult>(
        apiConfig.patients.merge(query),
      ),

    unmerge: (
      query: PatientUnmergeIds & {
        formContextIds?: string[] | undefined;
        reason: string;
        reasonDescription?: string;
      },
    ) => apiAxios.request(apiConfig.patients.unmerge(query)),

    getMhaDashboardData: (params: MhaDashboardDataParams) =>
      apiAxios.request<MhaDashboardDataResponse>(
        apiConfig.patients.getMhaDashboardData(params),
      ),

    getTimeline: (
      patientId: string,
      query: { limit?: number; offset?: number } = {},
    ) =>
      apiAxios.request<PatientTimelineResponse>(
        apiConfig.patients.getTimeline(patientId, query),
      ),
  },
  forms: {
    getFormContext: (formContextId: string) =>
      apiAxios.request<FormContextData | null>(
        apiConfig.forms.getFormContext(formContextId),
      ),
    getPdf: (
      formId: string,
      includeAllLinkedForms?: boolean,
      includeLinkedForms?: string[],
      version?: number,
    ) =>
      apiAxios.request<string | null>(
        apiConfig.forms.getPdf(
          formId,
          includeAllLinkedForms,
          includeLinkedForms,
          version,
        ),
      ),
    createSoadAssessmentContext: (
      formContextId: string,
      patientId: string,
      requestId: string,
    ) =>
      apiAxios.request<null>(
        apiConfig.forms.createSoadAssessmentContext(
          formContextId,
          patientId,
          requestId,
        ),
      ),
    createAdmissionsContext: (formContextId: string, patientId: string) =>
      apiAxios.request<null>(
        apiConfig.forms.createAdmissionsContext(formContextId, patientId),
      ),
    uploadPdfToExternalSystem: (
      formContextId: string,
      formId: string,
      externalPatientLinkId: string,
      filename: string,
      title: string,
      description: string,
      finalRevision: boolean,
      linkedFormIds?: string[],
    ) =>
      apiAxios.request<null>(
        apiConfig.forms.uploadPdfToExternalSystem(
          formContextId,
          formId,
          externalPatientLinkId,
          filename,
          title,
          description,
          finalRevision,
          linkedFormIds,
        ),
      ),
    getExternalPatientFormPushEvent: (formContextId: string, formId: string) =>
      apiAxios.request<GetExternalPatientFormPushEventResponse | null>(
        apiConfig.forms.getExternalPatientFormPushEvent(formContextId, formId),
      ),
    delete: (formId: string) =>
      apiAxios.request<null>(apiConfig.forms.delete(formId)),
    sign: (formId: string, createFormFromDraft: SignDraftRequest) =>
      apiAxios.request<null>(apiConfig.forms.sign(formId, createFormFromDraft)),
    requestAmend: (formId: string, amendRequest: RequestAmendRequest) =>
      apiAxios.request<null>(
        apiConfig.forms.requestAmend(formId, amendRequest),
      ),
    requestDeMinimis: (
      formId: string,
      body: {
        targetUserId: string;
        targetFormPart: number;
        errorType: string;
        note: string;
        selectedFields?: string[];
      },
    ) =>
      apiAxios.request<{ fileNoteId: number }>(
        apiConfig.forms.requestDeMinimis(formId, body),
      ),
    acceptDeMinimis: (formId: string, body: { fileNoteId: number }) =>
      apiAxios.request<void>(apiConfig.forms.acceptDeMinimis(formId, body)),
    completeDeMinimis: (
      formId: string,
      body: {
        fileNoteId: number;
        response: string;
        signature: string;
        signatoryName: string;
      },
    ) =>
      apiAxios.request<void>(apiConfig.forms.completeDeMinimis(formId, body)),
    rejectDeMinimis: (
      formId: string,
      body: { fileNoteId: number; rejectedReason: string },
    ) => apiAxios.request<void>(apiConfig.forms.rejectDeMinimis(formId, body)),
    cancelDeMinimis: (
      formId: string,
      body: { fileNoteId: number; reason: string },
    ) => apiAxios.request<void>(apiConfig.forms.cancelDeMinimis(formId, body)),
    requestRedo: (formId: string, body: RequestRedoRequest) =>
      apiAxios.request<null>(apiConfig.forms.requestRedo(formId, body)),
    initiateRedo: (formId: string, body: RequestRedoRequest) =>
      apiAxios.request<{ newFormId: string }>(
        apiConfig.forms.initiateRedo(formId, body),
      ),
    retractRedo: (formId: string, body: RedoReasonRequest) =>
      apiAxios.request<null>(apiConfig.forms.retractRedo(formId, body)),
    acceptRedo: (formId: string) =>
      apiAxios.request<{ newFormId: string }>(
        apiConfig.forms.acceptRedo(formId),
      ),
    declineRedo: (formId: string, body: RedoReasonRequest) =>
      apiAxios.request<null>(apiConfig.forms.declineRedo(formId, body)),
    cancelRedo: (formId: string, body: RedoReasonRequest) =>
      apiAxios.request<null>(apiConfig.forms.cancelRedo(formId, body)),
    addFileNote: (formId: string, fileNote: AddFileNote) =>
      apiAxios.request<null>(apiConfig.forms.addFileNote(formId, fileNote)),
    deleteFileNotes: (formId: string, fileNoteIds: { fileNoteIds: string }) =>
      apiAxios.request<null>(
        apiConfig.forms.deleteFileNotes(formId, fileNoteIds),
      ),
    create: (
      formId: string,
      createFormFromAssignmentRequest: CreateFormRequest,
    ) =>
      apiAxios.request<null>(
        apiConfig.forms.createFromAssignment(
          formId,
          createFormFromAssignmentRequest,
        ),
      ),
    getRioUploadData: (
      formContextId: string,
      formId: string,
      isBulkUpload?: boolean,
      linkedFormIds?: string[],
    ) =>
      apiAxios.request<RioUploadData>(
        apiConfig.forms.getRioUploadData(
          formContextId,
          formId,
          isBulkUpload,
          linkedFormIds,
        ),
      ),
    giveMyselfAccess: (formContextId: string) =>
      apiAxios.request<null>(apiConfig.forms.giveMyselfAccess(formContextId)),
    updateOfficialReferenceId: (
      formContextId: string,
      officialReferenceId: UpdateFormContextOfficialReferenceIdRequest["officialReferenceId"],
    ) =>
      apiAxios.request<null>(
        apiConfig.forms.updateOfficialReferenceId(
          formContextId,
          officialReferenceId,
        ),
      ),
    deleteUploadedForm(formId: string) {
      return apiAxios.request<null>(apiConfig.forms.deleteUploadedForm(formId));
    },
    updateAssessmentLocation: (
      formContextId: string,
      body: UpdateFormContextAssessmentLocationRequest,
    ) =>
      apiAxios.request<null>(
        apiConfig.forms.updateAssessmentLocation(formContextId, body),
      ),
  },
  teams: {
    addMember: (teamId: string, req: AddTeamMemberRequest) =>
      apiAxios.request(apiConfig.teams.addMember(teamId, req)),
    removeMember: (teamId: string, req: RemoveTeamMemberRequest) =>
      apiAxios.request(apiConfig.teams.removeMember(teamId, req)),
    create: (teamId: string, req: CreateTeamRequest) =>
      apiAxios.request(apiConfig.teams.create(teamId, req)),
    update: (teamId: string, req: UpdateTeamRequest) =>
      apiAxios.request(apiConfig.teams.update(teamId, req)),
    delete: (teamId: string, req: DeleteTeamRequest) =>
      apiAxios.request(apiConfig.teams.delete(teamId, req)),
    get: (teamId: string) =>
      apiAxios.request<ExtendedTeam | null>(apiConfig.teams.get(teamId)),
    search: (query: string, limit: number, offset: number) => {
      return apiAxios.request<
        any,
        AxiosResponse<{
          results: ExtendedTeam[];
          count: number;
        }>
      >(apiConfig.teams.search(query, limit, offset));
    },
    getLocalAuthorityMappings: (teamId: string) =>
      apiAxios.request<{ localAuthorityIds: string[] }>(
        apiConfig.teams.getLocalAuthorityMappings(teamId),
      ),
    getCareHomeGrants: (teamId: string) =>
      apiAxios.request<GetCareHomeGrantsResponse>(
        apiConfig.teams.getCareHomeGrants(teamId),
      ),
    setCareHomeGrants: (teamId: string, grants: TeamCareHomeGrant[]) =>
      apiAxios.request<void>(apiConfig.teams.setCareHomeGrants(teamId, grants)),
    setLocalAuthorityMappings: (teamId: string, localAuthorityIds: string[]) =>
      apiAxios.request<void>(
        apiConfig.teams.setLocalAuthorityMappings(teamId, localAuthorityIds),
      ),
    mergePreview: (primaryTeamId: string, secondaryTeamId: string) =>
      apiAxios.request<MergeTeamPreviewResponse>(
        apiConfig.teams.mergePreview(primaryTeamId, secondaryTeamId),
      ),
    merge: (primaryTeamId: string, body: MergeTeamRequest) =>
      apiAxios.request<{
        workItemsTransferred: number;
        workItemsMerged: number;
        membershipsAdded: number;
      }>(apiConfig.teams.merge(primaryTeamId, body)),
  },
  organisations: {
    addUserToOrganisation: (orgId: string, userId: string) =>
      apiAxios.request(
        apiConfig.organisations.addUserToOrganisation(orgId, userId),
      ),

    downloadAllUserEmails: (orgId: string) =>
      apiAxios.request(apiConfig.organisations.downloadAllUserEmails(orgId)),

    bulkUploadUsersToOrganisation: (
      orgId: string,
      emails: string[],
      dryRun?: boolean,
    ) =>
      apiAxios.request(
        apiConfig.organisations.bulkUploadUsersToOrganisation(
          orgId,
          emails,
          dryRun,
        ),
      ),

    removeUserFromOrganisation: (orgId: string, userId: string) =>
      apiAxios.request(
        apiConfig.organisations.removeUserFromOrganisation(orgId, userId),
      ),
    addMember: (teamId: string, req: AddOrganisationMemberRequest) =>
      apiAxios.request(apiConfig.organisations.addMember(teamId, req)),
    removeMember: (teamId: string, req: RemoveOrganisationMemberRequest) =>
      apiAxios.request(apiConfig.organisations.removeMember(teamId, req)),
    create: (orgId: string, req: CreateOrganisationRequest) =>
      apiAxios.request(apiConfig.organisations.create(orgId, req)),
    update: (orgId: string, req: UpdateOrganisationRequest) =>
      apiAxios.request(apiConfig.organisations.update(orgId, req)),
    delete: (orgId: string, req: DeleteOrganisationRequest) =>
      apiAxios.request(apiConfig.organisations.delete(orgId, req)),
    get: (orgId: string) =>
      apiAxios.request<any, AxiosResponse<GetOrganisationResponse>>(
        apiConfig.organisations.get(orgId),
      ),
    getDrFinderAssessingOrganisations: (localAuthorityId: string) =>
      apiAxios.request<GetDrFinderAssessingOrganisationsResponse>(
        apiConfig.organisations.getDrFinderAssessingOrganisations(
          localAuthorityId,
        ),
      ),
    setDrFinderAssessingOrganisations: (
      localAuthorityId: string,
      req: SetDrFinderAssessingOrganisationsRequest,
    ) =>
      // A refusal carries its reason; a 204 carries no body.
      apiAxios.request<{ reason: string } | "">(
        apiConfig.organisations.setDrFinderAssessingOrganisations(
          localAuthorityId,
          req,
        ),
      ),
    search: (query: string, limit: number, offset: number, role?: string) => {
      return apiAxios.request<
        any,
        AxiosResponse<{
          results: ExtendedOrganisation[];
          count: number;
        }>
      >(apiConfig.organisations.search(query, limit, offset, role));
    },
  },
  dataSharingPartnership: {
    create: (
      id: DataSharingPartnership["id"],
      name: DataSharingPartnership["name"],
    ) => {
      return apiAxios.request<void>(
        apiConfig.dataSharingPartnership.create(id, name),
      );
    },
    delete: (id: DataSharingPartnership["id"]) => {
      return apiAxios.request<void>(
        apiConfig.dataSharingPartnership.delete(id),
      );
    },
    addDataController: (
      id: DataSharingPartnership["id"],
      dataControllerId: string,
    ) => {
      return apiAxios.request<void>(
        apiConfig.dataSharingPartnership.addDataController(
          id,
          dataControllerId,
        ),
      );
    },
    removeDataController: (
      id: DataSharingPartnership["id"],
      dataControllerId: string,
    ) => {
      return apiAxios.request<void>(
        apiConfig.dataSharingPartnership.removeDataController(
          id,
          dataControllerId,
        ),
      );
    },
  },
  drafts: {
    create: (formDraftId: string, createDraft: CreateDraft) =>
      apiAxios.request<null>(apiConfig.drafts.create(formDraftId, createDraft)),
    get: (formDraftId: string) =>
      apiAxios.request<GetFormDraftResponse<any> | null>(
        apiConfig.drafts.get(formDraftId),
      ),
    getPdf: (formDraftId: string, review: boolean) =>
      apiAxios.request<string>(apiConfig.drafts.getPdf(formDraftId, review)),
    delete: (formDraftId: string) =>
      apiAxios.request<null>(apiConfig.drafts.delete(formDraftId)),
    updateData: (formDraftId: string, data: any) =>
      apiAxios.request<null>(apiConfig.drafts.updateData(formDraftId, data)),
  },
  rio: {
    instance: {
      create: (rioInstanceId: string, req: UpsertRioInstanceRequest) =>
        apiAxios.request(apiConfig.rio.instance.create(rioInstanceId, req)),
      update: (rioInstanceId: string, req: UpsertRioInstanceRequest) =>
        apiAxios.request(apiConfig.rio.instance.update(rioInstanceId, req)),
      delete: (rioInstanceId: string) =>
        apiAxios.request(apiConfig.rio.instance.delete(rioInstanceId)),
      testConnection: (rioInstanceId: string) =>
        apiAxios.request<RioConnectionTestResponse>(
          apiConfig.rio.instance.testConnection(rioInstanceId),
        ),
    },

    clickThrough: {
      createAndClaimToken: (
        req: CreateAndClaimOneTimeTokenForRioClickThroughInstanceRequest,
      ) =>
        apiAxios.request<CreateAndClaimOneTimeTokenForRioClickThroughInstanceResponse>(
          apiConfig.rio.clickThrough.createAndClaimToken(req),
        ),
      redeemToken: (req: RedeemOneTimeTokenForRioClickThroughInstanceRequest) =>
        apiAxios.request<RedeemOneTimeTokenForRioClickThroughInstanceResponse>(
          apiConfig.rio.clickThrough.redeemToken(req),
        ),
    },
  },
  tpp: {
    instance: {
      get: (tppInstanceId: string) =>
        apiAxios.request<TppConfiguration>(
          apiConfig.tpp.instance.get(tppInstanceId),
        ),
      create: (tppInstanceId: string, req: UpsertTppInstanceRequest) =>
        apiAxios.request(apiConfig.tpp.instance.create(tppInstanceId, req)),
      update: (tppInstanceId: string, req: UpsertTppInstanceRequest) =>
        apiAxios.request(apiConfig.tpp.instance.update(tppInstanceId, req)),
      delete: (tppInstanceId: string) =>
        apiAxios.request(apiConfig.tpp.instance.delete(tppInstanceId)),
      search: (query: string, limit: number = 10, offset: number = 0) =>
        apiAxios.request<{ results: TppConfiguration[]; count: number }>(
          apiConfig.tpp.instance.search(query, limit, offset),
        ),
    },
    clickThrough: {
      redeemToken: (req: RedeemOneTimeTokenForTppClickThroughInstanceRequest) =>
        apiAxios.request<RedeemOneTimeTokenForTppClickThroughInstanceResponse>(
          apiConfig.tpp.clickThrough.redeemToken(req),
        ),
    },
  },
  generic: {
    instance: {
      get: (genericInstanceId: string) =>
        apiAxios.request<GenericConfiguration>(
          apiConfig.generic.instance.get(genericInstanceId),
        ),
      create: (genericInstanceId: string, req: UpsertGenericInstanceRequest) =>
        apiAxios.request<{ reason?: string }>(
          apiConfig.generic.instance.create(genericInstanceId, req),
        ),
      update: (genericInstanceId: string, req: UpsertGenericInstanceRequest) =>
        apiAxios.request<{ reason?: string }>(
          apiConfig.generic.instance.update(genericInstanceId, req),
        ),
      delete: (genericInstanceId: string) =>
        apiAxios.request<{ reason?: string }>(
          apiConfig.generic.instance.delete(genericInstanceId),
        ),
      search: (query: string, limit: number = 10, offset: number = 0) =>
        apiAxios.request<{ results: GenericConfiguration[]; count: number }>(
          apiConfig.generic.instance.search(query, limit, offset),
        ),
    },
    clickThrough: {
      redeemToken: (
        req: RedeemOneTimeTokenForGenericClickThroughInstanceRequest,
      ) =>
        apiAxios.request<RedeemOneTimeTokenForGenericClickThroughInstanceResponse>(
          apiConfig.generic.clickThrough.redeemToken(req),
        ),
    },
  },
  eprClickThrough: {
    peek: (source: string, token: string) =>
      apiAxios.request<PeekClickThroughTokenResponse>(
        apiConfig.eprClickThrough.peek(source, token),
      ),
    links: {
      search: (query: string, limit: number = 10, offset: number = 0) =>
        apiAxios.request<EprUserLinkSearchResponse>(
          apiConfig.eprClickThrough.links.search(query, limit, offset),
        ),
      delete: (id: string) =>
        apiAxios.request<{ reason?: string }>(
          apiConfig.eprClickThrough.links.delete(id),
        ),
    },
  },
  supersetDashboards: {
    create: (dashboardId: string, req: UpsertSupersetDashboardRequest) =>
      apiAxios.request(apiConfig.supersetDashboards.create(dashboardId, req)),
    update: (dashboardId: string, req: UpsertSupersetDashboardRequest) =>
      apiAxios.request(apiConfig.supersetDashboards.update(dashboardId, req)),
    delete: (dashboardId: string) =>
      apiAxios.request(apiConfig.supersetDashboards.delete(dashboardId)),
  },
  templates: {
    compileTemplate: (template: string, templateData: TemplateData) =>
      apiAxios.request<string>(
        apiConfig.templates.compileTemplate(template, templateData),
      ),
  },
  placeOfSafety: {
    delete: (id: string) =>
      apiAxios.request<void>(apiConfig.placeOfSafety.delete(id)),
    create: (id: string, data: CreatePlaceOfSafetyRequest) =>
      apiAxios.request<CreatePlaceOfSafetyRequest>(
        apiConfig.placeOfSafety.create(id, data),
      ),
    update: (id: string, data: UpdatePlaceOfSafetyRequest) =>
      apiAxios.request<UpdatePlaceOfSafetyRequest>(
        apiConfig.placeOfSafety.update(id, data),
      ),
    getAll: () =>
      apiAxios.request<{
        results: CuratedPlaceOfSafety[];
        count: number;
      }>(apiConfig.placeOfSafety.search("", 1000, 0)),
  },
  trust: {
    getAll: () => apiAxios.request<Trust[]>(apiConfig.trust.getAll()),
    create: (trustId: string, data: CreateTrustRequest) =>
      apiAxios.request<CreateTrustRequest>(
        apiConfig.trust.create(trustId, data),
      ),
    update: (id: string, data: UpdateTrustRequest) =>
      apiAxios.request<UpdateTrustRequest>(apiConfig.trust.update(id, data)),
    delete: (id: string) => apiAxios.request<void>(apiConfig.trust.delete(id)),
    get: (id: string) =>
      apiAxios.request<Trust | null>(apiConfig.trust.get(id)),
  },
  mentalHealthCareProvider: {
    getAll: () =>
      apiAxios.request<MentalHealthCareProvider[]>(
        apiConfig.mentalHealthCareProvider.getAll(),
      ),
    create: (id: string, data: CreateMentalHealthCareProviderRequest) =>
      apiAxios.request<CreateMentalHealthCareProviderRequest>(
        apiConfig.mentalHealthCareProvider.create(id, data),
      ),
    update: (id: string, data: UpdateMentalHealthCareProviderRequest) =>
      apiAxios.request<UpdateMentalHealthCareProviderRequest>(
        apiConfig.mentalHealthCareProvider.update(id, data),
      ),
    delete: (id: string) =>
      apiAxios.request<unknown>(apiConfig.mentalHealthCareProvider.delete(id)),
  },
  londonCareRecord: {
    update: (
      id: string,
      data: Partial<CreateLondonCareRecordInstanceRequest>,
    ) =>
      apiAxios.request<Partial<CreateLondonCareRecordInstanceRequest>>(
        apiConfig.londonCareRecord.update(id, data),
      ),
    create: (trustId: string, data: CreateLondonCareRecordInstanceRequest) =>
      apiAxios.request<CreateLondonCareRecordInstanceRequest>(
        apiConfig.londonCareRecord.create(trustId, data),
      ),
    delete: (id: string) =>
      apiAxios.request<void>(apiConfig.londonCareRecord.delete(id)),
  },
  fhirApiClients: {
    getAll: () =>
      apiAxios.request<FhirApiClientResponse[]>(
        apiConfig.fhirApiClients.getAll(),
      ),
    getById: (id: string) =>
      apiAxios.request<FhirApiClientResponse>(
        apiConfig.fhirApiClients.getById(id),
      ),
    create: (data: Parameters<typeof apiConfig.fhirApiClients.create>[0]) =>
      apiAxios.request<FhirApiClientResponse>(
        apiConfig.fhirApiClients.create(data),
      ),
    update: (
      id: string,
      data: Parameters<typeof apiConfig.fhirApiClients.update>[1],
    ) =>
      apiAxios.request<FhirApiClientResponse>(
        apiConfig.fhirApiClients.update(id, data),
      ),
    delete: (id: string) =>
      apiAxios.request<void>(apiConfig.fhirApiClients.delete(id)),
    getAvailableDataControllers: () =>
      apiAxios.request<{ id: string; name: string; odsCode: string | null }[]>(
        apiConfig.fhirApiClients.getAvailableDataControllers(),
      ),
  },
  health: {
    version: () =>
      apiAxios.request<{ version: string }>(apiConfig.health.version()),
  },
  section12: {
    // Admin mutations
    createSpecialism: (body: CreateSection12SpecialismRequest) =>
      apiAxios.request<Section12Specialism>(
        apiConfig.section12.createSpecialism(body),
      ),
    updateSpecialism: (
      specialismId: string,
      body: UpdateSection12SpecialismRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.updateSpecialism(specialismId, body),
      ),
    deleteSpecialism: (specialismId: string) =>
      apiAxios.request<void>(
        apiConfig.section12.deleteSpecialism(specialismId),
      ),
    // Admin language mutations
    createLanguage: (body: CreateSection12LanguageRequest) =>
      apiAxios.request<Section12Language>(
        apiConfig.section12.createLanguage(body),
      ),
    updateLanguage: (
      languageId: string,
      body: UpdateSection12LanguageRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.updateLanguage(languageId, body),
      ),
    // User self-management mutations
    setUserAvailability: (
      userId: string,
      body: SetSection12AvailabilityBlocksRequestUnbranded,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserAvailability(userId, body),
      ),
    setUserSpecialisms: (
      userId: string,
      body: SetSection12SpecialismsRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserSpecialisms(userId, body),
      ),
    setUserLocalAuthorityMappings: (
      userId: string,
      body: SetSection12LocalAuthorityMappingsRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserLocalAuthorityMappings(userId, body),
      ),
    // Phone verification
    getPhoneStatus: (userId: string) =>
      apiAxios.request<Section12PhoneVerificationStatus>(
        apiConfig.section12.getPhoneStatus(userId),
      ),
    sendPhoneVerificationCode: (
      userId: string,
      body: SendSection12PhoneVerificationCodeRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.sendPhoneVerificationCode(userId, body),
      ),
    confirmPhone: (userId: string, body: ConfirmSection12PhoneRequest) =>
      apiAxios.request<void>(apiConfig.section12.confirmPhone(userId, body)),
    removePhone: (userId: string) =>
      apiAxios.request<void>(apiConfig.section12.removePhone(userId)),
    setUserGender: (userId: string, body: SetSection12GenderRequest) =>
      apiAxios.request<void>(apiConfig.section12.setUserGender(userId, body)),
    setUserLanguages: (userId: string, body: SetSection12LanguagesRequest) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserLanguages(userId, body),
      ),
    setUserTrustAffiliation: (
      userId: string,
      body: SetSection12TrustAffiliationRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserTrustAffiliation(userId, body),
      ),
    setUserMaxTravelTime: (
      userId: string,
      body: SetSection12MaxTravelTimeRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserMaxTravelTime(userId, body),
      ),
    setUserContactPreferences: (
      userId: string,
      body: SetSection12ContactPreferencesRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserContactPreferences(userId, body),
      ),
    setUserSpecialismsWithLevels: (
      userId: string,
      body: SetSection12SpecialismsWithLevelsRequest,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserSpecialismsWithLevels(userId, body),
      ),
    // User availability overrides
    getUserAvailabilityOverrides: (
      userId: string,
      range?: DateRangeUnbranded,
    ) =>
      apiAxios.request<GetSection12AvailabilityOverrideBlocksResponse>(
        apiConfig.section12.getUserAvailabilityOverrides(userId, range),
      ),
    setUserAvailabilityOverrides: (
      userId: string,
      body: SetSection12AvailabilityOverrideDatesRequestUnbranded,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setUserAvailabilityOverrides(userId, body),
      ),
    deleteUserAvailabilityOverrides: (
      userId: string,
      range: DateRangeUnbranded,
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.deleteUserAvailabilityOverrides(userId, range),
      ),
    // S12 Doctor Search
    searchDoctors: (localAuthorityId: string, excludeInterviewId?: string) =>
      apiAxios.request<Section12DoctorSearchResultUnbranded[]>(
        apiConfig.section12.searchDoctors(localAuthorityId, excludeInterviewId),
      ),
    // S12 Payment Requests
    getTeamInvoicing: (formContextId: string, amhpTeamId: string) =>
      apiAxios.request<Section12TeamInvoicing>(
        apiConfig.section12.getTeamInvoicing(formContextId, amhpTeamId),
      ),
    searchAmhpUsers: (query: string, limit: number, offset: number) =>
      apiAxios.request<{ results: UserSearchResult[]; totalCount: number }>(
        apiConfig.section12.searchAmhpUsers(query, limit, offset),
      ),
    createPaymentRequest: (body: CreateSection12PaymentRequestInput) =>
      apiAxios.request<Section12PaymentRequest>(
        apiConfig.section12.createPaymentRequest(body),
      ),
    getMyPaymentRequests: (params: {
      status?: Section12PaymentRequestStatusFilter;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }) =>
      apiAxios.request<{
        requests: Section12PaymentRequestWithDetails[];
        count: number;
      }>(apiConfig.section12.getMyPaymentRequests(params)),
    exportMyPaymentRequestsCsv: (params: {
      status?: Section12PaymentRequestStatusFilter;
      fromDate?: string;
      toDate?: string;
    }) =>
      apiAxios.request<Blob>(
        apiConfig.section12.exportMyPaymentRequestsCsv(params),
      ),
    getPaymentRequestsByLocalAuthorities: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }) =>
      apiAxios.request<{
        requests: Section12PaymentRequestWithDetails[];
        count: number;
      }>(apiConfig.section12.getPaymentRequestsByLocalAuthorities(params)),
    exportPaymentRequestsByLocalAuthoritiesCsv: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
    }) =>
      apiAxios.request<Blob>(
        apiConfig.section12.exportPaymentRequestsByLocalAuthoritiesCsv(params),
      ),
    getPaymentRequestsForFormContext: (formContextId: string) =>
      apiAxios.request<Section12PaymentRequestWithDetails[]>(
        apiConfig.section12.getPaymentRequestsForFormContext(formContextId),
      ),
    getPaymentRequestById: (paymentRequestId: string) =>
      apiAxios.request<Section12PaymentRequestWithDetails>(
        apiConfig.section12.getPaymentRequestById(paymentRequestId),
      ),
    getPaymentRequestReceiptDownloadUrl: (
      paymentRequestId: string,
      receiptId: string,
    ) =>
      apiAxios.request<Section12PaymentRequestReceiptDownloadResponse>(
        apiConfig.section12.getPaymentRequestReceiptDownloadUrl(
          paymentRequestId,
          receiptId,
        ),
      ),
    updatePaymentRequestStatus: (
      paymentRequestId: string,
      body: UpdateSection12PaymentRequestStatusInput,
    ) =>
      apiAxios.request<Section12PaymentRequest>(
        apiConfig.section12.updatePaymentRequestStatus(paymentRequestId, body),
      ),
    updatePaymentRequest: (
      paymentRequestId: string,
      body: UpdateSection12PaymentRequestInput,
    ) =>
      apiAxios.request<Section12PaymentRequest>(
        apiConfig.section12.updatePaymentRequest(paymentRequestId, body),
      ),
    limitedUpdatePaymentRequest: (
      paymentRequestId: string,
      body: LimitedUpdateSection12PaymentRequestInput,
    ) =>
      apiAxios.request<Section12PaymentRequest>(
        apiConfig.section12.limitedUpdatePaymentRequest(paymentRequestId, body),
      ),
    deletePaymentRequest: (paymentRequestId: string) =>
      apiAxios.request(
        apiConfig.section12.deletePaymentRequest(paymentRequestId),
      ),
    getAllPaymentRequests: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }) =>
      apiAxios.request<{
        results: Section12PaymentRequestWithDetails[];
        count: number;
      }>(apiConfig.section12.getAllPaymentRequests(params)),
    exportPaymentRequestsCsv: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      doctorUserId?: string;
      amhpTeamId?: string;
      localAuthorityId?: string;
    }) =>
      apiAxios.request<Blob>(
        apiConfig.section12.exportPaymentRequestsCsv(params),
      ),
    exportFinancePaymentRequestsCsv: (params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      doctorUserId?: string;
      amhpTeamId?: string;
      localAuthorityId?: string;
    }) =>
      apiAxios.request<Blob>(
        apiConfig.section12.exportFinancePaymentRequestsCsv(params),
      ),
    markPaymentRequestPaid: (paymentRequestId: string) =>
      apiAxios.request<Section12PaymentRequest>(
        apiConfig.section12.markPaymentRequestPaid(paymentRequestId),
      ),
    adminUpdatePaymentStatus: (
      paymentRequestId: string,
      body: UpdateSection12PaymentRequestAdminStatusInput,
    ) =>
      apiAxios.request<Section12PaymentRequest>(
        apiConfig.section12.adminUpdatePaymentStatus(paymentRequestId, body),
      ),
    adjustPaymentRequest: (
      paymentRequestId: string,
      body: AdjustSection12PaymentRequestInput,
    ) =>
      apiAxios.request<Section12PaymentRequest>(
        apiConfig.section12.adjustPaymentRequest(paymentRequestId, body),
      ),
    getPaymentRequestChangeHistory: (paymentRequestId: string) =>
      apiAxios.request<Section12PaymentRequestChange[]>(
        apiConfig.section12.getPaymentRequestChangeHistory(paymentRequestId),
      ),
    createXeroBill: (paymentRequestId: string, body?: { force?: boolean }) =>
      apiAxios.request<{ queued: boolean; blockReasons: string[] }>(
        apiConfig.section12.createXeroBill(paymentRequestId, body),
      ),
    // Home Base Location
    setHomeBaseLocation: (
      userId: string,
      body: {
        address: string | null;
        postalCode: string | null;
        useProfileAddress?: boolean;
      },
    ) =>
      apiAxios.request<void>(
        apiConfig.section12.setHomeBaseLocation(userId, body),
      ),
    // Route Times
    getRouteTimes: (body: { formContextId: string; userIds: string[] }) =>
      apiAxios.request<RouteTimeResult[]>(
        apiConfig.section12.getRouteTimes(body),
      ),
    // Calendar Sync
    getCalendarSyncConnections: (userId: string) =>
      apiAxios.request<GetCalendarSyncConnectionsResponse>(
        apiConfig.section12.getCalendarSyncConnections(userId),
      ),
    getCalendarSyncAuthUrl: (
      userId: string,
      body: GetCalendarSyncAuthUrlRequest,
    ) =>
      apiAxios.request<GetCalendarSyncAuthUrlResponse>(
        apiConfig.section12.getCalendarSyncAuthUrl(userId, body),
      ),
    exchangeCalendarSyncCode: (
      userId: string,
      body: ExchangeCalendarSyncCodeRequest,
    ) =>
      apiAxios.request<CalendarSyncConnection>(
        apiConfig.section12.exchangeCalendarSyncCode(userId, body),
      ),
    deleteCalendarSyncConnection: (userId: string, connectionId: string) =>
      apiAxios.request<void>(
        apiConfig.section12.deleteCalendarSyncConnection(userId, connectionId),
      ),
    setCalendarSyncEnabled: (userId: string, enabled: boolean) =>
      apiAxios.request<void>(
        apiConfig.section12.setCalendarSyncEnabled(userId, enabled),
      ),
    triggerCalendarSync: (userId: string, connectionId: string) =>
      apiAxios.request<void>(
        apiConfig.section12.triggerCalendarSync(userId, connectionId),
      ),
    triggerCalendarSyncAll: (userId: string) =>
      apiAxios.request<void>(
        apiConfig.section12.triggerCalendarSyncAll(userId),
      ),
    connectCalendarFeed: (userId: string, body: ConnectCalendarFeedRequest) =>
      apiAxios.request<CalendarSyncConnection>(
        apiConfig.section12.connectCalendarFeed(userId, body),
      ),
  },
  interviews: {
    create: (body: CreateInterviewInput) =>
      apiAxios.request<InterviewWithParticipants>(
        apiConfig.interviews.create(body),
      ),
    getForFormContext: (formContextId: string) =>
      apiAxios.request<InterviewWithParticipants[]>(
        apiConfig.interviews.getForFormContext(formContextId),
      ),
    getMyInterviews: (formContextId: string) =>
      apiAxios.request<DoctorInterviewView[]>(
        apiConfig.interviews.getMyInterviews(formContextId),
      ),
    get: (interviewId: string) =>
      apiAxios.request<InterviewWithParticipants>(
        apiConfig.interviews.get(interviewId),
      ),
    update: (interviewId: string, body: UpdateInterviewInput) =>
      apiAxios.request<InterviewWithParticipants>(
        apiConfig.interviews.update(interviewId, body),
      ),
    cancel: (interviewId: string) =>
      apiAxios.request<void>(apiConfig.interviews.cancel(interviewId)),
    addParticipant: (interviewId: string, body: AddInterviewParticipantInput) =>
      apiAxios.request<InterviewWithParticipants>(
        apiConfig.interviews.addParticipant(interviewId, body),
      ),
    confirmParticipant: (
      interviewId: string,
      participantId: string,
      body?: ConfirmParticipantInput,
    ) =>
      apiAxios.request<{
        revokedParticipants: { id: string; userId: string; teamId: string }[];
      }>(
        apiConfig.interviews.confirmParticipant(
          interviewId,
          participantId,
          body,
        ),
      ),
    revokeParticipant: (interviewId: string, participantId: string) =>
      apiAxios.request<{ revoked: boolean }>(
        apiConfig.interviews.revokeParticipant(interviewId, participantId),
      ),
    unconfirmParticipant: (interviewId: string, participantId: string) =>
      apiAxios.request<{ unconfirmed: boolean }>(
        apiConfig.interviews.unconfirmParticipant(interviewId, participantId),
      ),
    restoreParticipants: (interviewId: string) =>
      apiAxios.request<{ restoredCount: number }>(
        apiConfig.interviews.restoreParticipants(interviewId),
      ),
    getInvite: (shareCode: string) =>
      apiAxios.request<InterviewVerification>(
        apiConfig.interviews.getInvite(shareCode),
      ),
    acceptInvite: (shareCode: string, body?: AcceptInterviewInviteInput) =>
      apiAxios.request<void>(
        apiConfig.interviews.acceptInvite(shareCode, body),
      ),
    rejectInvite: (shareCode: string, body: RejectInterviewInviteInput) =>
      apiAxios.request<void>(
        apiConfig.interviews.rejectInvite(shareCode, body),
      ),
    getInviteRouteTime: (shareCode: string) =>
      apiAxios.request<RouteTimeResult | null>(
        apiConfig.interviews.getInviteRouteTime(shareCode),
      ),
    getRouteTimes: (interviewId: string, body: { userIds: string[] }) =>
      apiAxios.request<RouteTimeResult[]>(
        apiConfig.interviews.getRouteTimes(interviewId, body),
      ),
  },
  tortusAvt: {
    getLaunchToken: (body: GetTortusAvtLaunchTokenRequest) =>
      apiAxios.request<GetTortusAvtLaunchTokenResponse>(
        apiConfig.tortusAvt.getLaunchToken(body),
      ),
  },
  userSessionEvents: {
    record: (body: RecordUserSessionEventRequest) =>
      apiAxios.request(apiConfig.userSessionEvents.record(body)),
  },
};

/**
 * A reload that only schedules a request — bursts of it are coalesced, so there
 * is no response to hand the caller.
 */
export type ScheduleReload = () => void;

export type PusherChannelReloadResult<TResponse> = [
  UseAxiosResult<TResponse>[0],
  ScheduleReload,
  UseAxiosResult<TResponse>[2],
];

/**
 * Starts a refetch that nobody awaits. axios-hooks aborts a request once it is
 * superseded or its component unmounts, rejecting the refetch; that is routine,
 * so only any other failure is left to surface as an unhandled rejection.
 */
function refetchIgnoringCancellation(refetch: () => Promise<unknown>) {
  void refetch().catch((error: unknown) => {
    if (!isCancel(error)) {
      throw error;
    }
  });
}

/*
  Creates a hook that automatically reloads useAxios data based on events received on a pusher channel
 */
function usePusherChannelReload<TResponse, TRequestBody = undefined>(
  channelName: string,
  config: AxiosRequestConfig<TRequestBody> | string,
  options?: Options,
): PusherChannelReloadResult<TResponse> {
  const channel = useChannel(channelName);
  const result = useApiAxios<TResponse>(config as any, options);
  const reloadFn = debounce(() => {
    refetchIgnoringCancellation(result[1]);
  }, 500);
  useEvent(channel, NotificationEvents.resourceReload, () => {
    reloadFn();
  });
  return [result[0], reloadFn, result[2]];
}

export const apiHooks = {
  patientList: {
    // The endpoint refuses a caller who may not read a list, so a page that
    // cannot show one does not ask.
    list: function usePatientList(
      offset: number | undefined,
      limit: number | undefined,
      sortBy: PatientListSortBy | undefined,
      sortDirection: PatientListSortDirection | undefined,
      patientSearchFilter: string | undefined,
      mhaStatuses: MhaStatus[] | undefined,
      locationNames: string[] | undefined,
      skip: boolean,
    ) {
      return useApiAxios<PatientListResponse>(
        apiConfig.patientList.list(
          offset,
          limit,
          sortBy,
          sortDirection,
          patientSearchFilter,
          mhaStatuses,
          locationNames,
        ),
        { manual: skip },
      );
    },
  },
  capacityState: {
    register: function useCareHomeRegister(
      careHomeIds?: string[],
      residentSearchFilter?: string,
    ) {
      return useApiAxios<RegisterResponse>(
        apiConfig.capacityState.register(careHomeIds, residentSearchFilter),
      );
    },
    history: function useCapacityStateHistory(patientId: string) {
      return useApiAxios<CapacityStateWithContext[]>(
        apiConfig.capacityState.history(patientId),
      );
    },
  },
  s132: {
    record: function useS132Record(patientId: string, skip: boolean) {
      return useApiAxios<S132Record>(apiConfig.s132.record(patientId), {
        manual: skip,
      });
    },
    // Mount one live record per patient on a page, as `useChannel`'s
    // subscriptions require; anything else that shows the record should use
    // `record`.
    liveRecord: function useLiveS132Record(patientId: string) {
      return usePusherChannelReload<S132Record>(
        notificationChannelFns.s132(patientId),
        apiConfig.s132.record(patientId),
      );
    },
  },
  pathway: {
    getPatientPathways: function usePatientPathways() {
      return useApiAxios<PathwayPatientPathwaysResponse>(
        apiConfig.pathway.getPatientPathways(),
      );
    },
  },
  section12: {
    searchDoctorsAdmin: function useSearchS12DoctorsAdmin(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<Section12DoctorAdminSearchResult>(
        apiConfig.section12.searchDoctorsAdmin(query, limit, offset),
      );
    },
    getAllSpecialisms: function useGetAllSection12Specialisms() {
      return useApiAxios<Section12Specialism[]>(
        apiConfig.section12.getAllSpecialisms(),
      );
    },
    getAllLanguages: function useGetAllSection12Languages() {
      return useApiAxios<Section12Language[]>(
        apiConfig.section12.getAllLanguages(),
      );
    },
    searchDoctors: function useSearchS12Doctors(
      localAuthorityId: string | null,
      excludeInterviewId?: string,
    ) {
      return useApiAxios<Section12DoctorSearchResultUnbranded[]>(
        localAuthorityId
          ? apiConfig.section12.searchDoctors(
              localAuthorityId,
              excludeInterviewId,
            )
          : ({ url: "", method: "get" } as any),
        { manual: !localAuthorityId },
      );
    },
    getPaymentRequestsForFormContext:
      function useGetPaymentRequestsForFormContext(
        formContextId: string | null,
      ) {
        return useApiAxios<Section12PaymentRequestWithDetails[]>(
          formContextId
            ? apiConfig.section12.getPaymentRequestsForFormContext(
                formContextId,
              )
            : ({ url: "", method: "get" } as any),
          { manual: !formContextId },
        );
      },
    getMyPaymentRequests: function useGetMyPaymentRequests(params: {
      status?: Section12PaymentRequestStatusFilter;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }) {
      return useApiAxios<{
        requests: Section12PaymentRequestWithDetails[];
        count: number;
      }>(apiConfig.section12.getMyPaymentRequests(params));
    },
    getAllPaymentRequests: function useGetAllPaymentRequests(params: {
      status?: Section12PaymentRequestStatus;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }) {
      return useApiAxios<{
        requests: Section12PaymentRequestWithDetails[];
        count: number;
      }>(apiConfig.section12.getAllPaymentRequests(params));
    },
    getPaymentRequestsByLocalAuthorities:
      function useGetPaymentRequestsByLocalAuthorities(params: {
        status?: Section12PaymentRequestStatus;
        fromDate?: string;
        toDate?: string;
        limit?: number;
        offset?: number;
      }) {
        return useApiAxios<{
          requests: Section12PaymentRequestWithDetails[];
          count: number;
        }>(apiConfig.section12.getPaymentRequestsByLocalAuthorities(params));
      },
  },
  patientDemographics: {
    getAlerts: function useGetAlerts(trustIds?: string[]) {
      return useApiAxios<PatientDemographicsAlertsResponse>(
        apiConfig.patientDemographics.getAlerts(trustIds),
      );
    },
    getTrusts: function useGetDemographicsTrusts() {
      return useApiAxios<DashboardTrustsResponse>(
        apiConfig.patientDemographics.getTrusts(),
      );
    },
  },
  vision: {
    previewIncident: function usePreviewIncident(incidentId: string) {
      return useApiAxios<PreviewVisionIncidentReponse>(
        apiConfig.vision.previewIncident(incidentId),
      );
    },
    getIncidentForFormContext: function useGetIncidentForFormContext(
      formContextId: string,
    ) {
      return useApiAxios<ShareableIncidentDataV1Response | null>(
        apiConfig.vision.synchroniseIncidentForFormContext(formContextId),
      );
    },
    synchroniseIncidentForFormContext:
      function useSynchroniseIncidentForFormContext(
        incidentId: string,
        formContextId: string,
      ) {
        return usePusherChannelReload<ShareableIncidentDataV1Response | null>(
          notificationChannelFns.vision.nhs(incidentId),
          apiConfig.vision.synchroniseIncidentForFormContext(formContextId),
        );
      },
    synchroniseIncident: function useSynchroniseIncident(
      incidentId: string,
      formContextId: string,
    ) {
      return usePusherChannelReload<ShareableIncidentDataV1Response | null>(
        notificationChannelFns.vision.nhs(incidentId),
        apiConfig.vision.synchroniseIncidentForFormContext(formContextId),
      );
    },
  },

  outcomeForm: {
    getForFormContext: function useOutcomeFormsForFormContext(
      formContextId: string,
    ) {
      return usePusherChannelReload<OutcomeFormSummary[]>(
        notificationChannelFns.formContext(formContextId),
        apiConfig.outcomeForm.getForFormContext(formContextId),
      );
    },
  },

  singleUseActionToken: {
    getAll: function useSingleUseActionTokenGetAll() {
      return useApiAxios<GetAllSingleUseActionTokensResponse>(
        apiConfig.singleUseActionToken.getAll(),
      );
    },
    get: function useSingleUseActionTokenGet(token: UUID) {
      return useApiAxios<GetSingleUseActionTokenResponse>(
        apiConfig.singleUseActionToken.get(token),
      );
    },
  },

  reports: {
    availableOrgsAndTeams: function useAvailableOrgsAndTeams(params: {
      teamId?: string;
      orgId?: string;
    }) {
      return useApiAxios<OrgsAndTeamsResult>(
        apiConfig.reports.availableOrgsAndTeams(params),
      );
    },
    dashboardData: function useDashboardData(params: ReportContextParams) {
      return useApiAxios<DashboardData>(
        apiConfig.reports.getDashboardData(params),
      );
    },
    nhsDashboardData: function useNhsDashboardData(organisationId: string) {
      return useApiAxios<{
        dashboardId: string;
        embedUrl: string;
        embedToken: string;
      }>(apiConfig.reports.getNhsDashboardData(organisationId));
    },
    supersetDashboards: function useSupersetDashboards(
      organisationId: string | null,
    ) {
      return useApiAxios<OrganisationSupersetDashboard[]>(
        apiConfig.reports.getSupersetDashboards(organisationId ?? ""),
        { manual: !organisationId },
      );
    },
    supersetDashboardData: function useSupersetDashboardData(
      organisationId: string,
      supersetDashboardId: string,
    ) {
      return useApiAxios<SupersetEmbedConfig>(
        apiConfig.reports.getSupersetDashboardData(
          organisationId,
          supersetDashboardId,
        ),
        { manual: !organisationId || !supersetDashboardId },
      );
    },
    recordMhaDashboardView: function useRecordMhaDashboardView(
      organisationId: string | null,
    ) {
      return useApiAxios<void>(
        apiConfig.reports.recordMhaDashboardView(organisationId ?? ""),
        { manual: !organisationId },
      );
    },
    mhaDashboardLastViewed: function useMhaDashboardLastViewed(
      organisationId: string | null,
    ) {
      return useApiAxios<{ lastViewedAt: string | null }>(
        apiConfig.reports.getMhaDashboardLastViewed(organisationId ?? ""),
        { manual: !organisationId },
      );
    },
  },
  configuration: {
    getPdsConfiguration: function usePdsConfiguration() {
      return useApiAxios<{ apiKey: string; endpoint: string; jwtKid: string }>(
        apiConfig.configuration.getPdsConfiguration(),
      );
    },
    getRafConfiguration: function useRafConfiguration() {
      return useApiAxios<{ apiKey: string; endpoint: string; jwtKid: string }>(
        apiConfig.configuration.getRafConfiguration(),
      );
    },
    getAdminManagedLocations: function useAdminManagedLocations() {
      return useApiAxios<ManagedLocationsResponse>(
        apiConfig.configuration.getLocations(),
      );
    },
    getApprovedEmailDomains: function useApprovedEmailDomains() {
      return useApiAxios<{ pattern: string }[]>(
        apiConfig.configuration.getApprovedEmailDomains(),
      );
    },
    getEmailRedirects: function useEmailRedirects() {
      return useApiAxios<
        {
          id: string;
          sourceEmail: string;
          targetTeamId: string;
          message: string | null;
          createdAt: string;
          createdByUserId: string | null;
        }[]
      >(apiConfig.configuration.getEmailRedirects());
    },
    getS12ApprovalConfiguration: function useS12ApprovalConfiguration() {
      return useApiAxios<{ endpoint: string; username: string }>(
        apiConfig.configuration.getS12ApprovalConfiguration(),
      );
    },
    getOdsConfiguration: function useOdsConfiguration() {
      return useApiAxios<{ endpoint: string; apiKey: string }>(
        apiConfig.configuration.getOdsConfiguration(),
      );
    },
    getXeroConfiguration: function useXeroConfiguration() {
      return useApiAxios<{
        clientId: string;
        tenantId: string;
        enabled: boolean;
        autoCreateContacts: boolean;
        configuredRateAccountCode: string;
        mileageAccountCode: string;
        otherAccountCode: string;
        dueDateDays: number;
        maxPaymentTotalPence: number;
        maxRequestsPerDoctorPerDay: number;
        maxRequestsPerPatientPerDay: number;
        maxOtherAmountPence: number;
        maxMileageAmountPence: number;
        isConnected: boolean;
      }>(apiConfig.configuration.getXeroConfiguration());
    },
  },

  work: {
    retrieveWork: function useWorkItemGet(
      userId: string,
      contextId: string,
      mode: "caseload" | "archive" | "user-caseload",
      params: {
        limit?: number;
        offset?: number;
        patientSearchFilter?: string;
        selectedFormTemplateIds?: string[];
        selectedSenderNames?: string[];
        fromDate?: string;
        toDate?: string;
      },
    ) {
      return usePusherChannelReload<
        GetWorkResponse,
        { patientSearchFilter?: string }
      >(
        mode === "user-caseload"
          ? notificationChannelFns.work.userItems(userId)
          : mode === "archive"
            ? notificationChannelFns.work.archive(contextId)
            : notificationChannelFns.work.items(contextId),
        apiConfig.work.retrieveWork(mode, params),
      );
    },
  },
  audit: {
    searchByFormContextId: function useSearchByResourceId(
      formContextId: string,
      params: {
        limit?: number;
        offset?: number;
        fromDate: string;
        toDate: string;
      },
    ) {
      return useApiAxios<{ entries: AuditTrailEntry[]; count: number }>(
        apiConfig.audit.searchByFormContextId(formContextId, params),
      );
    },

    searchByPatientId: function useSearchByPatientId(
      patientId: string,
      params: {
        limit?: number;
        offset?: number;
        fromDate: string;
        toDate: string;
      },
    ) {
      return useApiAxios<{ entries: AuditTrailEntry[]; count: number }>(
        apiConfig.audit.searchByPatientId(patientId, params),
      );
    },
    search: function useSearchByResourceId(params: {
      limit?: number;
      offset?: number;
      fromDate: string;
      toDate: string;
      resourceId: string | null;
      type: AuditTrailEntryType[] | null;
      userId: UUID | null;
    }) {
      return useApiAxios<{ entries: AuditTrailEntry[]; count: number }>(
        apiConfig.audit.search(params),
      );
    },
    searchByResourceId: function useSearchByResourceId(
      resourceId: string,
      params: {
        limit?: number;
        offset?: number;
        fromDate: string;
        toDate: string;
      },
    ) {
      return useApiAxios<{ entries: AuditTrailEntry[]; count: number }>(
        apiConfig.audit.searchByResourceId(resourceId, params),
      );
    },
  },
  forms: {
    getFormContext: function useFormsGet(formContextId: string) {
      return usePusherChannelReload<FormContextData | null>(
        notificationChannelFns.formContext(formContextId),
        apiConfig.forms.getFormContext(formContextId),
      );
    },
    get: function useFormsGet(formId: string) {
      return usePusherChannelReload<GetFormResponse<any> | null>(
        notificationChannelFns.form(formId),
        apiConfig.forms.get(formId),
      );
    },
    getPdf: function useFormsGetPdf(
      formId: string,
      includeLinkedForms?: boolean,
    ) {
      return usePusherChannelReload<string | null>(
        notificationChannelFns.form(formId),
        apiConfig.forms.getPdf(formId, includeLinkedForms),
      );
    },

    getFileNotes: function useGetFileNotes(formId: string) {
      return useApiAxios<FileNote[]>(apiConfig.forms.getFileNotes(formId));
    },
    uploadFormPreview: function useUploadFormPreview(
      formContextId: string,
      formId: string,
      data: UploadFormRequest,
    ) {
      return useApiAxios<string>(
        apiConfig.forms.uploadFormPreview(formContextId, formId, data),
      );
    },
    getExternalPatientFormPushEvent:
      function useGetExternalPatientFormPushEvent(
        formContextId: string,
        formId: string,
      ) {
        return usePusherChannelReload<GetExternalPatientFormPushEventResponse>(
          notificationChannelFns.formContext(formContextId),
          apiConfig.forms.getExternalPatientFormPushEvent(
            formContextId,
            formId,
          ),
        );
      },
  },
  notifications: {
    get: function useNotificationsGet(
      teamId: string | undefined,
      query: {
        limit?: number;
        offset?: number;
      } = {},
    ) {
      return usePusherChannelReload<NotificationsResponse>(
        notificationChannelFns.team.notifications(teamId),
        apiConfig.notifications.get(query),
      );
    },
  },
  teams: {
    get: function useTeamGet(teamId: string) {
      return usePusherChannelReload<ExtendedTeam | null>(
        notificationChannelFns.team.profile(teamId),
        apiConfig.teams.get(teamId),
      );
    },
    search: function useTeamSearch(
      query: string,
      limit: number,
      offset: number,
      ancestorOrganisationId?: string,
    ) {
      return useApiAxios<{
        results: ExtendedTeam[];
        count: number;
      }>(apiConfig.teams.search(query, limit, offset, ancestorOrganisationId));
    },
  },
  organisations: {
    get: function useOrgGet(organisationId: string | null) {
      return useApiAxios<GetOrganisationResponse | null>(
        // The empty id is never requested: manual suppresses the fetch until an id is supplied.
        apiConfig.organisations.get(organisationId ?? ""),
        { manual: !organisationId },
      );
    },
    search: function useOrgSearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<{
        results: ExtendedOrganisation[];
        count: number;
      }>(apiConfig.organisations.search(query, limit, offset));
    },
  },
  dataSharingPartnership: {
    getAll: function useDspGetAll() {
      return useApiAxios<DataSharingPartnershipWithDataControllers[]>(
        apiConfig.dataSharingPartnership.getAll(),
      );
    },
  },
  users: {
    search: function useUserSearch(
      query: string,
      limit: number,
      offset: number,
      managingOrganisationId?: string,
    ) {
      return useApiAxios<{
        results: Omit<ExtendedThalamosUser, "defaultSignature">[];
        count: number;
      }>(apiConfig.users.search(query, limit, offset, managingOrganisationId));
    },
    me: function useUserMe(contextId: string | null) {
      const data = useApiAxios<ExtendedThalamosUser | null>(
        apiConfig.users.me(contextId),
      );

      if (data[0]?.response?.status === 200) {
        currentUserId = data[0].data!.id;
      }

      return data;
    },
    getUserViaPusher: function useGetUser(userId: string) {
      return usePusherChannelReload<ExtendedThalamosUser | null>(
        notificationChannelFns.user.profile(userId),
        apiConfig.users.get(userId),
      );
    },

    getUserDetails: function useGetUser(userId: string) {
      return useApiAxios<ExtendedThalamosUser | null>(
        apiConfig.users.get(userId),
      );
    },

    getDeviceSessions: function useGetDeviceSessions(userId: string) {
      return useApiAxios<UserDeviceSession[]>(
        apiConfig.users.getDeviceSessions(userId),
      );
    },

    getOrganisationConfiguration: function useGetUserOrganisationConfiguration(
      userId: string,
    ) {
      return useApiAxios<UserOrganisationConfigurationSummary[]>(
        apiConfig.users.getOrganisationConfiguration(userId),
      );
    },
  },
  placeOfSafety: {
    search: function usePlaceOfSafetySearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<{
        results: CuratedPlaceOfSafety[];
        count: number;
      }>(apiConfig.placeOfSafety.search(query, limit, offset));
    },
  },
  trust: {
    getAll: function useTrustGetAll() {
      return useApiAxios<Trust[]>(apiConfig.trust.getAll());
    },
    search: function useTrustSearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<{
        results: Trust[];
        count: number;
      }>(apiConfig.trust.search(query, limit, offset));
    },
  },
  mentalHealthCareProvider: {
    getAll: function useGetAllMentalHealthCareProviders() {
      return useApiAxios<MentalHealthCareProvider[]>(
        apiConfig.mentalHealthCareProvider.getAll(),
      );
    },
    search: function useMentalHealthCareProviderSearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<{
        results: MentalHealthCareProvider[];
        count: number;
      }>(apiConfig.mentalHealthCareProvider.search(query, limit, offset));
    },
  },
  patients: {
    get: function useGetPatient(patientId: string) {
      return usePusherChannelReload<GetPatientResponse | null>(
        notificationChannelFns.patient(patientId),
        apiConfig.patients.get(patientId),
      );
    },

    getExternalPatientLinks: function useGetExternalPatientLinks(
      patientId: string,
      query: {
        patientIdsToTreatAsRootPatients: string[];
      },
    ) {
      return usePusherChannelReload<GetExternalPatientLinksResponse>(
        notificationChannelFns.patientTimeline(patientId),
        apiConfig.patients.getExternalPatientLinks(patientId, query),
      );
    },

    getTimeline: function useGetPatientTimeline(
      patientId: string,
      query: {
        limit?: number;
        offset?: number;
      } = {},
    ) {
      return usePusherChannelReload<PatientTimelineResponse>(
        notificationChannelFns.patientTimeline(patientId),
        apiConfig.patients.getTimeline(patientId, query),
      );
    },

    getTimelineWithoutSubscription: function useGetPatientTimeline(
      patientId: string,
      query: {
        limit?: number;
        offset?: number;
      } = {},
    ) {
      return useApiAxios<PatientTimelineResponse>(
        apiConfig.patients.getTimeline(patientId, query),
      );
    },

    // RAF responses must never be served from a cache.
    getNhsFlags: function useGetNhsFlags(patientId: string, skip: boolean) {
      return useApiAxios<ReasonableAdjustmentsFlagResponse>(
        apiConfig.patients.getNhsFlags(patientId),
        { manual: skip },
      );
    },

    getMhaState: function useGetMhaState(
      patientId: string,
      query: {
        limit: number;
        offset: number;
        patientIdsToTreatAsRootPatients?: string[];
      },
    ) {
      return usePusherChannelReload<{ states: PatientStateWithContext[] }>(
        notificationChannelFns.mhaStatus(patientId),
        apiConfig.patients.getMhaState(patientId, query),
      );
    },

    // Shares the mha-status pusher channel: every write path that can change
    // the resolved place of safety already notifies on this channel, so this
    // stays live without its own separate notification wiring. `skip` when
    // the viewing org doesn't display MHA status (patientStateEnabled), since
    // display — not calculation — governs whether this is shown, and the data
    // exists regardless.
    //
    // Deliberately does not use usePusherChannelReload: that helper's reload
    // callback ignores `manual` and always re-executes the request, which
    // would fire a live network call on every mha-status channel event even
    // while skipped. Subscribing to the channel is harmless; only the actual
    // refetch needs to be gated on `skip`.
    getSection136PlaceOfSafety: function useGetSection136PlaceOfSafety(
      patientId: string,
      skip: boolean,
    ) {
      const channel = useChannel(notificationChannelFns.mhaStatus(patientId));
      const result = useApiAxios<GetSection136PlaceOfSafetyResponse>(
        apiConfig.patients.getSection136PlaceOfSafety(patientId),
        { manual: skip },
      );
      const reloadFn = debounce(() => {
        if (!skip) {
          refetchIgnoringCancellation(result[1]);
        }
      }, 500);
      useEvent(channel, NotificationEvents.resourceReload, () => {
        reloadFn();
      });
      return result;
    },

    getMhaDashboardData: function useGetMhaDashboardData(
      params: MhaDashboardDataParams,
    ) {
      // TODO: add pusher channel
      return useApiAxios<
        MhaDashboardDataResponse,
        { patientSearchFilter?: string }
      >(apiConfig.patients.getMhaDashboardData(params));
    },

    getMhaDashboardTrusts: function useGetMhaDashboardTrusts() {
      return useApiAxios<DashboardTrustsResponse>(
        apiConfig.patients.getMhaDashboardTrusts(),
      );
    },

    search: function useSearchPatient(query: PatientIndexQuery) {
      return useApiAxios<PatientIndexSearchResult>(
        apiConfig.patients.search(query),
      );
    },
  },
  soadUserTeamOrg: {
    getSoads: function useGetSoads(cqcOrgId: string) {
      return useApiAxios<SoadListResponse>(
        apiConfig.soadUserTeamOrg.getSoads(cqcOrgId),
      );
    },
  },
  soad: {
    getPdf: function useGetPdfDraft(
      formContextId: string,
      formId: string,
      version?: number,
    ) {
      return useApiAxios<string>(
        apiConfig.soad.getPdf(formContextId, formId, version),
      );
    },
  },
  drafts: {
    get: function useGetDraft(formDraftId: string) {
      return usePusherChannelReload<GetFormDraftResponse<any> | null>(
        notificationChannelFns.formDraft(formDraftId),
        apiConfig.drafts.get(formDraftId),
      );
    },
    getPdf: function useGetPdfDraft(formDraftId: string, review: boolean) {
      return useApiAxios<string>(apiConfig.drafts.getPdf(formDraftId, review));
    },
  },
  rio: {
    get: function useRiosGet(rioInstanceId: string) {
      return useApiAxios<RioConfiguration | null>(
        apiConfig.rio.instance.get(rioInstanceId),
      );
    },
    search: function useRioSearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<{ results: RioConfiguration[]; count: number }>(
        apiConfig.rio.instance.search(query, limit, offset),
      );
    },
  },

  tpp: {
    get: function useTppGet(tppInstanceId: string) {
      return useApiAxios<TppConfiguration | null>(
        apiConfig.tpp.instance.get(tppInstanceId),
      );
    },
    search: function useTppSearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<{ results: TppConfiguration[]; count: number }>(
        apiConfig.tpp.instance.search(query, limit, offset),
      );
    },
  },

  generic: {
    get: function useGenericGet(genericInstanceId: string) {
      return useApiAxios<GenericConfiguration | null>(
        apiConfig.generic.instance.get(genericInstanceId),
      );
    },
    search: function useGenericSearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<{ results: GenericConfiguration[]; count: number }>(
        apiConfig.generic.instance.search(query, limit, offset),
      );
    },
  },

  eprClickThrough: {
    searchLinks: function useEprUserLinksSearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<EprUserLinkSearchResponse>(
        apiConfig.eprClickThrough.links.search(query, limit, offset),
      );
    },
  },

  supersetDashboards: {
    get: function useSupersetDashboardGet(dashboardId: string) {
      return useApiAxios<SupersetDashboard | null>(
        apiConfig.supersetDashboards.get(dashboardId),
        { manual: !dashboardId },
      );
    },
    getDataGlossary: function useSupersetDashboardDataGlossary(
      dashboardId: string,
    ) {
      return useApiAxios<{ dataGlossaryMarkdown: string | null }>(
        apiConfig.supersetDashboards.getDataGlossary(dashboardId),
      );
    },
    search: function useSupersetDashboardSearch(
      query: string,
      limit: number,
      offset: number,
    ) {
      return useApiAxios<SupersetDashboardSearchResult>(
        apiConfig.supersetDashboards.search(query, limit, offset),
      );
    },
  },

  termsAndConditions: {
    get: function useTermsAndConditionsGet(id: UUID) {
      return useApiAxios<TermsAndConditions | null>(
        apiConfig.termsAndConditions.get(id),
      );
    },
    getAll: function useTermsAndConditionsGetAll() {
      return useApiAxios<TermsAndConditionsResponse>(
        apiConfig.termsAndConditions.getAll(),
      );
    },
  },

  londonCareRecord: {
    getLondonCareRecordConfiguration:
      function useLondonCareRecordConfiguration() {
        return useApiAxios<LondonCareRecordConfig>(
          apiConfig.londonCareRecord.getLondonCareRecordConfiguration(),
        );
      },
  },

  fhirApiClients: {
    getAll: function useGetAllFhirApiClients() {
      return useApiAxios<FhirApiClientResponse[]>(
        apiConfig.fhirApiClients.getAll(),
      );
    },
    getById: function useGetFhirApiClientById(id: string | null) {
      return useApiAxios<FhirApiClientResponse>(
        id
          ? apiConfig.fhirApiClients.getById(id)
          : ({ url: "", method: "get" } as any),
        { manual: !id },
      );
    },
    getAvailableDataControllers: function useGetAvailableDataControllers() {
      return useApiAxios<
        { id: string; name: string; odsCode: string | null }[]
      >(apiConfig.fhirApiClients.getAvailableDataControllers());
    },
  },

  interviews: {
    getForFormContext: function useGetInterviewsForFormContext(
      formContextId: string | null,
    ) {
      return useApiAxios<InterviewWithParticipants[]>(
        formContextId
          ? apiConfig.interviews.getForFormContext(formContextId)
          : ({ url: "", method: "get" } as any),
        { manual: !formContextId },
      );
    },
    get: function useGetInterview(interviewId: string | null) {
      return useApiAxios<InterviewWithParticipants>(
        interviewId
          ? apiConfig.interviews.get(interviewId)
          : ({ url: "", method: "get" } as any),
        { manual: !interviewId },
      );
    },
    getInvite: function useGetInvite(shareCode: string | null) {
      return useApiAxios<InterviewVerification>(
        shareCode
          ? apiConfig.interviews.getInvite(shareCode)
          : ({ url: "", method: "get" } as any),
        { manual: !shareCode },
      );
    },
  },
  ods: {
    getLocalAuthorityByOdsCode: function useGetLocalAuthorityByOdsCode(
      odsCode: string | null,
    ) {
      const config = useMemo(
        () =>
          odsCode
            ? apiConfig.ods.getLocalAuthorityByOdsCode(odsCode)
            : ({ url: "", method: "get" } as any),
        [odsCode],
      );
      return useApiAxios<LocalAuthorityDetails>(config, {
        manual: !odsCode,
      });
    },
  },
};

export const logout = () => {
  localStorage.removeItem("userId");
  localStorage.removeItem("teamId");
  localStorage.removeItem("roleId");
  window.location.href = `https://${config.auth0.domain}/v2/logout?client_id=${
    config.auth0.clientId
  }&returnTo=${encodeURIComponent(`${config.backendUrl}/api/oauth/logout`)}`;
};
